import { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { Plus, DollarSign, Eye, XCircle, CreditCard, Banknote, ArrowRightLeft, QrCode, Search, Package, Trash2, Filter, ShoppingBag, Check, LayoutGrid, AlertTriangle, ArrowRight, Download, Printer, Receipt, Loader2, Star, FileText } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import { Sale, SaleStatus, PaymentMethod, SaleType, Product } from '@/types';
import { useDiscountReasons } from '@/hooks/useDiscountReasons';
import DiscountReasonsModal from '@/components/DiscountReasonsModal';
import { useSales, useSale, useCreateSale, useCancelSale } from '@/hooks/useSales';
import { useProducts, useCategories } from '@/hooks/useProducts';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { SaleItemCreate, salesService, SaleUpdate } from '@/services/sales';
import { loyaltyService, LoyaltyCustomer } from '@/services/loyalty';
import { useCashRegisterStatus } from '@/hooks/useCashRegister';
import { formatPrice, formatDateTime, parseARNumber, formatARNumber } from '@/utils/format';
import { printSaleTicket, printElectronicTicket } from '@/utils/receipt';
import { printFiscal, reprintFiscal, notaCreditoFiscal, getFiscalAgentUrl, setFiscalAgentUrl, CIERRE_Z_PENDIENTE } from '@/utils/fiscal';
import toast from 'react-hot-toast';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { useAuthStore } from '@/context/authStore';
import { generateSaleReceipt } from '@/services/pdfService';
import {
  Unit,
  Dimension,
  compatibleUnits,
  toBase,
  formatStock,
  formatPricePerUnit,
  pricePerDisplayFromBase,
  pricePerBaseFromDisplay,
  UNIT_LABEL_SHORT,
} from '@/utils/units';

type ViewTab = 'history' | 'new';

// Etiqueta de contenido de un combo: "2x Galleta, 0,25 kg Harina"
const comboContentsLabel = (items?: { quantity: number; display_name?: string; component_display_unit?: string }[]) =>
  (items ?? [])
    .map((ci) => {
      const qty = Number(ci.quantity) || 0;
      const unit = ci.component_display_unit;
      if (unit && unit !== 'unit') return `${qty} ${UNIT_LABEL_SHORT[unit as Unit]} ${ci.display_name ?? ''}`.trim();
      return `${qty > 1 ? `${qty}x ` : ''}${ci.display_name ?? ''}`.trim();
    })
    .join(', ');

const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: 'Todos los métodos' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'transfer', label: 'Transferencia' },
];

// Reglas de canje de puntos VIP (deben coincidir con el backend loyalty_service.py)
/**
 * Errores del controlador fiscal. Cuando el agente identifica que lo único que falta
 * es el Cierre Z, el mensaje ya viene explicado y con la instrucción: lo mostramos
 * solo (sin prefijo) y más tiempo, para que el cajero alcance a leerlo.
 */
const toastFiscalError = (e: any, prefijo: string, sufijo = '') => {
  if (e?.identificador === CIERRE_Z_PENDIENTE) {
    toast.error(e.message, { duration: 12000 });
    return;
  }
  toast.error(`${prefijo}: ${e?.message || 'agente fiscal no disponible'}${sufijo}`);
};

/** N° de comprobante electrónico con el formato de ARCA: 0005-00000002 */
const nroComprobanteE = (sale: Pick<Sale, 'e_pto_vta' | 'e_cbte_nro'>) =>
  `${String(sale.e_pto_vta ?? '').padStart(4, '0')}-${String(sale.e_cbte_nro ?? '').padStart(8, '0')}`;

/** Chapita "Factura-e" para el listado: de un vistazo se ve qué venta se facturó por ARCA */
const FacturaEBadge = ({ sale, className = '' }: { sale: Sale; className?: string }) =>
  sale.cae ? (
    <span
      title={`Factura C electrónica — CAE ${sale.cae}`}
      className={`inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-700 px-2 py-0.5 text-[10px] font-semibold font-sans ${className}`}
    >
      <Receipt className="h-3 w-3" /> Factura-e {nroComprobanteE(sale)}
    </span>
  ) : null;

// Fecha de HOY en formato YYYY-MM-DD, en hora local (no UTC: a la noche difieren)
const hoyLocalISO = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const LOYALTY_REDEEM_POINTS = 1000;
const LOYALTY_REDEEM_VALUE = 30000;
const LOYALTY_MIN_AMOUNT = 10000;     // mínimo (sobre el total pagado) para sumar puntos
const LOYALTY_PESOS_PER_POINT = 1000; // $ por punto
const pointsForAmount = (amount: number) =>
  amount >= LOYALTY_MIN_AMOUNT ? Math.floor(amount / LOYALTY_PESOS_PER_POINT) : 0;

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  qr: 'QR',
  other: 'Otro',
};

const PAYMENT_METHOD_ICONS: Record<PaymentMethod, React.ReactNode> = {
  cash: <Banknote className="h-5 w-5" />,
  card: <CreditCard className="h-5 w-5" />,
  transfer: <ArrowRightLeft className="h-5 w-5" />,
  qr: <QrCode className="h-5 w-5" />,
  other: <DollarSign className="h-5 w-5" />,
};

const STATUS_LABELS: Record<SaleStatus, string> = {
  pending: 'Pendiente',
  completed: 'Completada',
  cancelled: 'Cancelada',
};

const STATUS_COLORS: Record<SaleStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const TYPE_LABELS: Record<SaleType, string> = {
  direct: 'Directa',
  wholesale: 'Mayorista',
};

// Tipo para items del carrito (puede ser producto o item personalizado)
interface CartItem {
  id: string; // ID único para el carrito (product.id o generado para custom)
  product?: Product; // undefined para items personalizados
  customName?: string; // Nombre para items personalizados
  quantity: number;             // En quantity_unit (lo que ingresa el usuario)
  quantity_unit: Unit;          // Unidad elegida (default = display_unit del producto, o 'unit' para custom)
  customPrice?: number;         // Precio personalizado en $/quantity_unit (lo que ve el usuario)
  comboId?: string;             // Si pertenece a un combo armado en el momento, ID del grupo
  comboLabel?: string;          // Nombre del combo ad-hoc
  saleMode?: 'unit' | 'pack';   // 'pack' = venta por caja (usa pack_price, descuenta N unidades)
}

export default function Sales() {
  const { user, tenant } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  // Cancelar/eliminar ventas: admin, superadmin y también empleado (misma función)
  const canCancelSale = isAdmin || user?.role === 'employee';
  // El empleado puede mirar días anteriores, pero SIN los importes: solo ve la
  // plata del día en curso. Tampoco ve los totales del local (recuadros de arriba).
  const montosSoloHoy = user?.role === 'employee';
  const hoyISO = hoyLocalISO();
  // El empleado no ve los TOTALES DEL PERIODO de dias anteriores (los recuadros de
  // arriba, que dependen de `rangoEsHoy`), pero si el importe de cada venta: sin eso
  // no puede emitir una nota de credito ni un comprobante de una venta de ayer,
  // porque le figura en cero.

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ViewTab>('history');

  // Leer tab del query param al cargar
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'new') {
      setActiveTab(tabParam);
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Filtros historial
  const [paymentFilter, setPaymentFilter] = useState<PaymentMethod | ''>('');
  // Para el empleado el filtro arranca (y queda) en hoy; para el resto, sin filtro
  const [dateFrom, setDateFrom] = useState<string>(() => (montosSoloHoy ? hoyLocalISO() : ''));
  const [dateTo, setDateTo] = useState<string>(() => (montosSoloHoy ? hoyLocalISO() : ''));
  // El empleado sí ve los totales del DÍA (le sirven para la caja); lo que no ve
  // son los de días anteriores, así que los recuadros aparecen solo con el filtro en hoy.
  const rangoEsHoy = dateFrom === hoyISO && dateTo === hoyISO;

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<Sale | null>(null);
  const [ncConfirm, setNcConfirm] = useState<Sale | null>(null);
  const [printPrompt, setPrintPrompt] = useState<Sale | null>(null);
  // Que puede ofrecerse para el ticket recien hecho. Un comprobante sin venta queda
  // solo con el ticket comun: no se factura por ningun camino.
  const facturable = !!printPrompt && !printPrompt.is_placeholder;
  const ofreceFiscalHasar = facturable && !!tenant?.fiscal_enabled && !printPrompt?.cae;
  const ofreceFiscalElectronica = facturable && !!tenant?.electronic_invoice_enabled;
  const [fiscalPrinting, setFiscalPrinting] = useState(false);
  const [emittingElectronic, setEmittingElectronic] = useState(false);
  // Agregar DNI a una venta ya hecha (desde el ojito) para sumar puntos VIP luego
  const [ojitoDni, setOjitoDni] = useState('');
  const [ojitoDniLoading, setOjitoDniLoading] = useState(false);
  // Editar una venta ya hecha (solo admin): medio de pago + descuento
  const [editingSale, setEditingSale] = useState<Sale | null>(null);
  const [editPayment, setEditPayment] = useState<PaymentMethod>('cash');
  const [editSplit, setEditSplit] = useState(false);
  const [editSplitAmt, setEditSplitAmt] = useState<Record<PaymentMethod, string>>({ cash: '', card: '', transfer: '', qr: '', other: '' });
  const [editDiscType, setEditDiscType] = useState<'percentage' | 'fixed'>('percentage');
  const [editDiscValue, setEditDiscValue] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Nueva venta
  const [newSaleItems, setNewSaleItems] = useState<CartItem[]>([]);
  const [confirmCheckout, setConfirmCheckout] = useState(false);
  const [newSalePayment, setNewSalePayment] = useState<PaymentMethod>('cash');
  // Puntos VIP: cliente encontrado por DNI + si se canjean puntos en esta venta
  const [vipCustomer, setVipCustomer] = useState<LoyaltyCustomer | null>(null);
  const [vipLoading, setVipLoading] = useState(false);
  const [vipRedeem, setVipRedeem] = useState(false);
  const [consultingPoints, setConsultingPoints] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerDocument, setCustomerDocument] = useState('');

  const [newSaleNotes, setNewSaleNotes] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Item personalizado (Otro)
  const [showCustomItemForm, setShowCustomItemForm] = useState(false);
  const [customItemName, setCustomItemName] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState('');

  // Combo armado en el momento (ad-hoc)
  const [showComboBuilder, setShowComboBuilder] = useState(false);
  const [comboName, setComboName] = useState('');
  const [comboPrice, setComboPrice] = useState('');
  const [comboSelections, setComboSelections] = useState<{ product: Product; quantity: number }[]>([]);
  const [comboProductSearch, setComboProductSearch] = useState('');

  // Motivo del descuento: obligatorio cuando se descuenta. Sin esto el total
  // descontado del mes no se puede explicar.
  const [discountReasonId, setDiscountReasonId] = useState<number | ''>('');
  const [showReasonsModal, setShowReasonsModal] = useState(false);
  const [directDiscountType, setDirectDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [directDiscountValue, setDirectDiscountValue] = useState('');

  // Pago dividido: el cajero ingresa a mano cuánto entró por cada medio (solo trackeo)
  // Comprobante sin venta ("viandas", "almuerzo"): imprime el ticket para entregar
  // pero no mueve plata. La venta real se cobra por otra via.
  const [placeholderMode, setPlaceholderMode] = useState(false);
  const [splitMode, setSplitMode] = useState(false);
  const [splitCash, setSplitCash] = useState('');
  const [splitCard, setSplitCard] = useState('');
  const [splitTransfer, setSplitTransfer] = useState('');
  const [splitQr, setSplitQr] = useState('');
  // Descuento (%) por cada medio de pago del split (lo calcula el sistema)
  const [splitCashDisc, setSplitCashDisc] = useState('');
  const [splitCardDisc, setSplitCardDisc] = useState('');
  const [splitTransferDisc, setSplitTransferDisc] = useState('');
  const [splitQrDisc, setSplitQrDisc] = useState('');

  // Queries
  // Fechas en hora Argentina (-03:00): así el filtro por día coincide con la caja
  // y no arrastra ventas de la noche al día siguiente (que en UTC ya es otro día).
  const { data: sales = [], isLoading } = useSales({
    payment_method: paymentFilter || undefined,
    date_from: dateFrom ? `${dateFrom}T00:00:00-03:00` : undefined,
    date_to: dateTo ? `${dateTo}T23:59:59-03:00` : undefined,
  });
  const { data: saleDetail } = useSale(selectedSale?.id ?? null);
  const { data: categories = [] } = useCategories();
  const { data: discountReasons = [] } = useDiscountReasons();
  const queryClient = useQueryClient();

  // Emite Nota de Crédito fiscal + cancela la venta + devuelve stock. Reutilizable
  // desde el ojito y desde el modal de cancelación (cuando la venta ya se facturó).
  const emitirNotaCreditoYCancelar = async (sale: Sale) => {
    setFiscalPrinting(true);
    try {
      // 1) NC fiscal (revierte el comprobante ante AFIP)
      const n = await notaCreditoFiscal(sale, { cuit: tenant?.cuit });
      // 2) Cancelar la venta en el sistema (devuelve el stock)
      try {
        await cancelSale.mutateAsync(sale.id);
        toast.success(`NC emitida${n ? ` — N° ${n}` : ''}. Venta cancelada y stock devuelto.`);
      } catch {
        toast.error('La NC fiscal salió OK, pero no se pudo cancelar la venta. Cancelala a mano para devolver el stock.');
      }
      setIsDetailModalOpen(false);
      setCancelConfirm(null);
    } catch (e: any) {
      toastFiscalError(e, 'No se pudo emitir la NC');
    } finally {
      setFiscalPrinting(false);
    }
  };

  // Emite la Factura C electrónica por ARCA (backend -> WSFE) y la imprime en la
  // térmica. Si la venta ya tiene CAE, solo reimprime (no vuelve a emitir).
  const handleEmitElectronic = async (sale: Sale) => {
    const store = { name: tenant?.name, cuit: tenant?.cuit, address: tenant?.address };
    if (sale.cae) {
      await printElectronicTicket(sale, store);
      return;
    }
    setEmittingElectronic(true);
    try {
      const updated = await salesService.emitElectronicInvoice(sale.id);
      const nro = `${String(updated.e_pto_vta ?? '').padStart(4, '0')}-${String(updated.e_cbte_nro ?? '').padStart(8, '0')}`;
      toast.success(`Factura C electrónica emitida — N° ${nro}`);
      // Reflejar el CAE en el modal ANTES de imprimir: si la impresión falla, el modal
      // queda abierto y el botón de la Hasar tiene que estar ya deshabilitado.
      setPrintPrompt((prev) => (prev && prev.id === sale.id ? updated : prev));
      await printElectronicTicket(updated, store);
      queryClient.invalidateQueries({ queryKey: ['sale', sale.id] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      setPrintPrompt(null);
    } catch (e: any) {
      toast.error(`No se pudo emitir: ${e?.response?.data?.detail || e?.message || 'error ARCA'}`);
    } finally {
      setEmittingElectronic(false);
    }
  };

  // SIEMPRE cargar productos - más simple para el usuario.
  // Si los combos están deshabilitados, no los traemos al listado de venta.
  const { data: allProducts = [], isLoading: isLoadingProducts } = useProducts(
    { only_active: true, is_combo: tenant?.combos_enabled === false ? false : undefined },
    { enabled: activeTab === 'new' }
  );

  const { data: cashStatus } = useCashRegisterStatus();

  // Verificar si tiene permiso de caja
  const hasCashPermission = user?.effective_permissions?.includes('cash_register');
  const isCashClosed = hasCashPermission && cashStatus && !cashStatus.is_open;

  // Mutations
  const createSale = useCreateSale();
  const cancelSale = useCancelSale();

  // Filtrar productos según búsqueda y categoría
  const filteredProducts = useMemo(() => {
    let result = allProducts;

    // Filtrar por búsqueda
    if (productSearch.length >= 2) {
      const search = productSearch.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(search) ||
        (p.code && p.code.toLowerCase().includes(search))
      );
    }

    // Filtrar por categoría
    if (categoryFilter !== 'all') {
      result = result.filter(p => p.category_id === parseInt(categoryFilter));
    }

    // Ordenar alfabéticamente por nombre
    result = [...result].sort((a, b) =>
      a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
    );

    return result;
  }, [allProducts, productSearch, categoryFilter]);

  // Precio único de venta por base_unit (el método de pago no cambia el precio;
  // el descuento por efectivo se aplica manualmente abajo).
  // Helpers para venta por caja (bulto)
  const packUnitsOf = (item: CartItem): number => Math.max(1, Number(item.product?.units_per_pack ?? 1));
  const isPackSale = (item: CartItem): boolean =>
    item.saleMode === 'pack' && !!item.product?.pack_price && packUnitsOf(item) > 1;
  // Cantidad efectiva en base_unit (para totales y stock). En caja: cajas × unidades.
  const baseQtyForItem = (item: CartItem): number =>
    isPackSale(item) ? item.quantity * packUnitsOf(item) : toBase(item.quantity, item.quantity_unit);

  const priceForItem = (item: CartItem): number => {
    if (!item.product) {
      return pricePerBaseFromDisplay(item.customPrice ?? 0, item.quantity_unit);
    }
    // Venta por caja: precio por base = precio de la caja / unidades por caja
    if (isPackSale(item)) {
      return Number(item.product.pack_price) / packUnitsOf(item);
    }
    if (item.customPrice != null) {
      return pricePerBaseFromDisplay(item.customPrice, item.quantity_unit);
    }
    return item.product.sale_price ?? 0;
  };

  const newSaleTotal = useMemo(() => {
    return newSaleItems.reduce(
      (sum, item) => sum + priceForItem(item) * baseQtyForItem(item),
      0
    );
  }, [newSaleItems]);

  const directDiscountNum = parseFloat(directDiscountValue) || 0;
  const newSaleTotalWithDiscount = (() => {
    if (directDiscountNum <= 0) return newSaleTotal;
    if (directDiscountType === 'percentage') return Math.max(0, newSaleTotal * (1 - directDiscountNum / 100));
    return Math.max(0, newSaleTotal - directDiscountNum);
  })();

  // Pago dividido: por cada medio, el NETO = monto − descuento% (lo calcula el sistema).
  // Los montos vienen en formato AR (1.234,56) -> parseARNumber.
  const splitGross = (amt: string) => parseARNumber(amt) || 0;
  const splitNet = (amt: string, disc: string) => {
    const g = splitGross(amt);
    const d = Math.min(100, Math.max(0, parseFloat(disc) || 0));
    return Math.max(0, g * (1 - d / 100));
  };
  const splitCashNum = splitNet(splitCash, splitCashDisc);
  const splitCardNum = splitNet(splitCard, splitCardDisc);
  const splitTransferNum = splitNet(splitTransfer, splitTransferDisc);
  const splitQrNum = splitNet(splitQr, splitQrDisc);
  const splitSum = splitCashNum + splitCardNum + splitTransferNum + splitQrNum;

  // Canje de puntos VIP: solo sin pago dividido y si el cliente tiene saldo suficiente.
  const canRedeemVip = !!vipCustomer && vipCustomer.points_balance >= LOYALTY_REDEEM_POINTS;
  const loyaltyRedeemActive = vipRedeem && canRedeemVip && !splitMode;
  const loyaltyDiscount = loyaltyRedeemActive ? Math.min(LOYALTY_REDEEM_VALUE, newSaleTotalWithDiscount) : 0;

  // En split el total cobrado es la suma ingresada; si no, el total con descuento (y canje VIP).
  const effectiveTotal = splitMode ? splitSum : Math.max(0, newSaleTotalWithDiscount - loyaltyDiscount);

  // Consulta puntual del saldo de puntos (botón al lado del DNI). Fuerza la búsqueda
  // al instante y muestra el saldo en un aviso, sin esperar el debounce.
  const handleConsultarPuntos = async () => {
    const dni = customerDocument.trim();
    if (dni.length < 6) { toast('Ingresá el DNI del cliente', { icon: '🔎' }); return; }
    setConsultingPoints(true);
    try {
      const c = await loyaltyService.lookup(dni);
      setVipCustomer(c);
      if (c) {
        toast.success(`${c.name || `DNI ${c.dni}`}: ${c.points_balance} puntos VIP`, { icon: '⭐' });
      } else {
        toast(`DNI ${dni}: sin puntos (cliente nuevo)`, { icon: '⭐' });
      }
    } catch {
      toast.error('No se pudo consultar los puntos');
    } finally {
      setConsultingPoints(false);
    }
  };

  // Buscar el cliente VIP por DNI (con debounce) para mostrar su saldo de puntos.
  useEffect(() => {
    const dni = customerDocument.trim();
    if (dni.length < 6) { setVipCustomer(null); setVipRedeem(false); setVipLoading(false); return; }
    let cancelled = false;
    setVipLoading(true);
    const t = setTimeout(async () => {
      try {
        const c = await loyaltyService.lookup(dni);
        if (!cancelled) { setVipCustomer(c); if (!c) setVipRedeem(false); }
      } catch {
        if (!cancelled) setVipCustomer(null);
      } finally {
        if (!cancelled) setVipLoading(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [customerDocument]);

  const hasItemsWithoutPrice = useMemo(() => {
    return newSaleItems.some(item => {
      // Item personalizado sin precio
      if (!item.product) {
        return item.customPrice == null || item.customPrice <= 0;
      }
      // En modo caja el precio es el de la caja (siempre presente)
      if (isPackSale(item)) return false;
      // Producto sin precio configurado
      return item.product.sale_price == null && item.customPrice == null;
    });
  }, [newSaleItems]);

  const handleViewDetail = (sale: Sale) => {
    setSelectedSale(sale);
    setIsDetailModalOpen(true);
  };

  // Editar venta (admin): inicializa el modal desde la venta
  const openEditSale = (sale: Sale) => {
    setEditingSale(sale);
    setEditReason('');
    setEditPayment((sale.payment_method as PaymentMethod) || 'cash');
    setEditSplit(!!sale.is_split);
    setEditSplitAmt({
      cash: sale.amount_cash ? String(Number(sale.amount_cash)) : '',
      card: sale.amount_card ? String(Number(sale.amount_card)) : '',
      transfer: sale.amount_transfer ? String(Number(sale.amount_transfer)) : '',
      qr: sale.amount_qr ? String(Number(sale.amount_qr)) : '',
      other: '',
    });
    if (Number(sale.discount_percentage) > 0) {
      setEditDiscType('percentage'); setEditDiscValue(String(Number(sale.discount_percentage)));
    } else if (Number(sale.discount_amount) > 0) {
      setEditDiscType('fixed'); setEditDiscValue(String(Number(sale.discount_amount)));
    } else {
      setEditDiscType('percentage'); setEditDiscValue('');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingSale) return;
    if (!editReason.trim()) { toast.error('Escribí el motivo del cambio'); return; }
    setEditSaving(true);
    try {
      const discNum = parseFloat(editDiscValue) || 0;
      const payload: SaleUpdate = editSplit
        ? {
            reason: editReason.trim(),
            is_split: true,
            amount_cash: parseFloat(editSplitAmt.cash) || 0,
            amount_card: parseFloat(editSplitAmt.card) || 0,
            amount_transfer: parseFloat(editSplitAmt.transfer) || 0,
            amount_qr: parseFloat(editSplitAmt.qr) || 0,
          }
        : {
            reason: editReason.trim(),
            is_split: false,
            payment_method: editPayment,
            discount_percentage: editDiscType === 'percentage' ? discNum : 0,
            discount_amount: editDiscType === 'fixed' ? discNum : 0,
          };
      await salesService.editSale(editingSale.id, payload);
      toast.success('Venta actualizada');
      const id = editingSale.id;
      setEditingSale(null);
      setIsDetailModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['sale', id] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No se pudo editar la venta');
    } finally {
      setEditSaving(false);
    }
  };

  // Agrega el DNI a una venta ya hecha (ojito) y acredita los puntos retroactivamente
  const handleAssignDni = async () => {
    if (!saleDetail || !ojitoDni.trim()) return;
    setOjitoDniLoading(true);
    try {
      await salesService.assignLoyaltyDni(saleDetail.id, ojitoDni.trim());
      toast.success('DNI agregado — puntos acreditados');
      setOjitoDni('');
      await queryClient.invalidateQueries({ queryKey: ['sale', saleDetail.id] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No se pudo agregar el DNI');
    } finally {
      setOjitoDniLoading(false);
    }
  };

  const handleCancel = async () => {
    if (cancelConfirm) {
      try {
        await cancelSale.mutateAsync(cancelConfirm.id);
        setCancelConfirm(null);
      } catch (error) {
        console.error('Error cancelling:', error);
      }
    }
  };

  const handleAddProduct = (product: Product) => {
    const existing = newSaleItems.find((item) => item.product?.id === product.id);
    if (existing) {
      setNewSaleItems(
        newSaleItems.map((item) =>
          item.product?.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      setNewSaleItems([...newSaleItems, {
        id: `product-${product.id}`,
        product,
        quantity: 1,
        quantity_unit: ((product.display_unit as Unit) || 'unit'),
        customPrice: undefined,
      }]);
    }
  };

  // Pistola de código de barras: al escanear, buscamos por `code` y sumamos al carrito.
  useBarcodeScanner({
    enabled: activeTab === 'new',
    onScan: (code) => {
      const match = allProducts.find((p) => (p.code || '').trim() === code);
      if (match) {
        handleAddProduct(match);
        toast.success(`Agregado: ${match.name}`);
      } else {
        toast.error(`Código ${code} sin producto asignado`);
      }
    },
  });

  const handleAddCustomItem = () => {
    if (!customItemName.trim() || !customItemPrice) return;
    const newItem: CartItem = {
      id: `custom-${Date.now()}`,
      customName: customItemName.trim(),
      quantity: 1,
      quantity_unit: 'unit',
      customPrice: parseFloat(customItemPrice)
    };
    setNewSaleItems([...newSaleItems, newItem]);
    setCustomItemName('');
    setCustomItemPrice('');
    setShowCustomItemForm(false);
  };

  const handleUpdatePrice = (itemId: string, price: number | undefined) => {
    setNewSaleItems(
      newSaleItems.map((item) =>
        item.id === itemId ? { ...item, customPrice: price } : item
      )
    );
  };

  const handleRemoveItem = (itemId: string) => {
    const target = newSaleItems.find((item) => item.id === itemId);
    // Si el item es parte de un combo ad-hoc, se quita todo el grupo
    if (target?.comboId) {
      setNewSaleItems(newSaleItems.filter((item) => item.comboId !== target.comboId));
      return;
    }
    setNewSaleItems(newSaleItems.filter((item) => item.id !== itemId));
  };

  // === Combo ad-hoc ===
  const comboSelTotalNormal = useMemo(() => {
    return comboSelections.reduce((sum, s) => {
      const base = Number(s.product.sale_price ?? 0);
      const qtyBase = toBase(s.quantity, (s.product.display_unit as Unit) || 'unit');
      return sum + base * qtyBase;
    }, 0);
  }, [comboSelections]);

  const toggleComboProduct = (product: Product) => {
    setComboSelections((prev) => {
      const exists = prev.find((s) => s.product.id === product.id);
      if (exists) return prev.filter((s) => s.product.id !== product.id);
      return [...prev, { product, quantity: 1 }];
    });
  };

  const setComboQty = (productId: number, quantity: number) => {
    if (quantity <= 0) return;
    setComboSelections((prev) =>
      prev.map((s) => (s.product.id === productId ? { ...s, quantity } : s))
    );
  };

  const resetComboBuilder = () => {
    setShowComboBuilder(false);
    setComboName('');
    setComboPrice('');
    setComboSelections([]);
    setComboProductSearch('');
  };

  const handleAddCombo = () => {
    const price = parseARNumber(comboPrice) ?? 0;
    const sel = comboSelections.filter((s) => s.quantity > 0);
    if (!comboName.trim() || price <= 0 || sel.length === 0) return;

    const comboId = `combo-${Date.now()}`;
    // Distribuir el precio promo entre los productos, proporcional a su precio normal (efectivo)
    const weights = sel.map((s) => {
      const base = Number(s.product.sale_price ?? 0);
      const qtyBase = toBase(s.quantity, (s.product.display_unit as Unit) || 'unit');
      return base * qtyBase;
    });
    const totalWeight = weights.reduce((a, b) => a + b, 0);

    const newItems: CartItem[] = sel.map((s, i) => {
      const share = totalWeight > 0 ? price * (weights[i] / totalWeight) : price / sel.length;
      const unit = (s.product.display_unit as Unit) || 'unit';
      const perDisplay = s.quantity > 0 ? share / s.quantity : share; // $/display-unit (efectivo)
      return {
        id: `${comboId}-${s.product.id}`,
        product: s.product,
        quantity: s.quantity,
        quantity_unit: unit,
        customPrice: perDisplay,
        comboId,
        comboLabel: comboName.trim(),
      };
    });

    setNewSaleItems([...newSaleItems, ...newItems]);
    resetComboBuilder();
  };

  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) return;
    setNewSaleItems(
      newSaleItems.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  // Cambiar entre venta por unidad y por caja (bulto). Al cambiar, reinicia la cantidad a 1.
  const handleSetSaleMode = (itemId: string, mode: 'unit' | 'pack') => {
    setNewSaleItems(
      newSaleItems.map((item) =>
        item.id === itemId ? { ...item, saleMode: mode, quantity: 1, quantity_unit: mode === 'pack' ? 'unit' : item.quantity_unit } : item
      )
    );
  };

  const handleUpdateQuantityUnit = (itemId: string, newUnit: Unit) => {
    setNewSaleItems(
      newSaleItems.map((item) =>
        item.id === itemId ? { ...item, quantity_unit: newUnit } : item
      )
    );
  };

  const handleCreateSale = async () => {
    if (newSaleItems.length === 0) return;
    if (hasItemsWithoutPrice) return;
    // El backend tambien lo valida; aca se corta antes para no mandar una venta
    // que ya sabemos que va a volver rechazada.
    if (directDiscountNum > 0 && discountReasonId === '') {
      toast.error('Elegí el motivo del descuento');
      return;
    }

    const items: SaleItemCreate[] = newSaleItems.map((item) => {
      // Item personalizado (sin producto): unit_price ya es el total por unidad.
      if (!item.product) {
        return {
          custom_name: item.customName,
          quantity: item.quantity,
          quantity_unit: 'unit',
          unit_price: item.customPrice ?? 0,
        };
      }

      // Venta por caja: se manda en UNIDADES (cajas × unidades) con precio por unidad
      // derivado (precio_caja / unidades). Así el stock descuenta las unidades reales.
      if (isPackSale(item)) {
        const packUnits = packUnitsOf(item);
        return {
          product_id: item.product.id,
          quantity: item.quantity * packUnits,
          quantity_unit: 'unit' as Unit,
          unit_price: Number(item.product.pack_price) / packUnits,
        };
      }

      // Producto normal: precio único de venta en $/base (sin recargo por método).
      const unitPriceBase = item.customPrice != null
        ? pricePerBaseFromDisplay(item.customPrice, item.quantity_unit)
        : (item.product.sale_price ?? 0);
      return {
        product_id: item.product.id,
        quantity: item.quantity,
        quantity_unit: item.quantity_unit,
        unit_price: unitPriceBase,
      };
    });

    try {
      const createdSale = await createSale.mutateAsync({
        sale_type: 'direct',
        customer_name: customerName.trim() || undefined,
        customer_phone: customerPhone.trim() || undefined,
        customer_document: customerDocument.trim() || undefined,
        items,
        // En split el payment_method es solo un placeholder; el desglose va en los montos.
        payment_method: splitMode ? 'cash' : newSalePayment,
        is_placeholder: placeholderMode,
        discount_reason_id: directDiscountNum > 0 && discountReasonId !== '' ? discountReasonId : undefined,
        loyalty_redeem: loyaltyRedeemActive && !placeholderMode,
        notes: newSaleNotes || undefined,
        ...(splitMode
          ? {
              is_split: true,
              amount_cash: splitCashNum || undefined,
              amount_card: splitCardNum || undefined,
              amount_transfer: splitTransferNum || undefined,
              amount_qr: splitQrNum || undefined,
            }
          : {
              discount_percentage: directDiscountType === 'percentage' && directDiscountNum > 0 ? directDiscountNum : undefined,
              discount_amount: directDiscountType === 'fixed' && directDiscountNum > 0 ? directDiscountNum : undefined,
            }),
      });

      // En vez de imprimir automáticamente, ofrecer imprimir en un modal
      if (createdSale) {
        setPrintPrompt(createdSale);
      }

      setConfirmCheckout(false);
      setNewSaleItems([]);
      setNewSalePayment('cash');
      setVipCustomer(null);
      setVipRedeem(false);
      setCustomerName('');
      setCustomerPhone('');
      setCustomerDocument('');
      setNewSaleNotes('');
      setDirectDiscountValue('');
      setDirectDiscountType('percentage');
      setDiscountReasonId('');
      setSplitMode(false);
      setPlaceholderMode(false);
      setSplitCash(''); setSplitCard(''); setSplitTransfer(''); setSplitQr('');
      setSplitCashDisc(''); setSplitCardDisc(''); setSplitTransferDisc(''); setSplitQrDisc('');
      setActiveTab('history');
    } catch (error: any) {
      console.error('Error creating sale:', error);
      // Si se corto la conexion no sabemos si la venta llego a registrarse: puede
      // haberse creado en el server y haberse perdido la respuesta. Avisar de mirar
      // el historial antes de recobrar, o se duplica la venta y el descuento de stock.
      const sinRespuesta =
        error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK' || !error?.response;
      if (sinRespuesta) {
        toast.error(
          'Se cortó la conexión con el servidor y no se pudo confirmar la venta. ' +
          'Antes de volver a cobrar, fijate en el historial si quedó registrada: ' +
          'si está, no la cobres de nuevo.',
          { duration: 15000 }
        );
      } else {
        const detalle = error?.response?.data?.detail;
        toast.error(typeof detalle === 'string' ? detalle : 'No se pudo registrar la venta.');
      }
    }
  };

  const formatCurrency = formatPrice;
  const formatDate = formatDateTime;

  const totals = useMemo(() => {
    const result = {
      cash: 0, card: 0, transfer: 0, qr: 0, other: 0, total: 0,
      // Facturado = lo que se declaró ante ARCA, por cualquiera de los dos caminos.
      // Es el número que importa para Monotributo; el resto salió con ticket común.
      facturadoFiscal: 0, facturadoElectronica: 0, facturado: 0,
      cantFiscal: 0, cantElectronica: 0,
    };
    sales.forEach((sale) => {
      // Un comprobante sin venta no es plata: queda en el listado pero fuera de
      // todos los totales, igual que en caja y en los reportes del backend.
      if (sale.status !== 'cancelled' && !sale.is_placeholder) {
        const saleTotal = Number(sale.total) || 0;
        if (sale.is_split) {
          // Pago dividido: distribuir por los montos cobrados de cada medio
          result.cash += Number(sale.amount_cash) || 0;
          result.card += Number(sale.amount_card) || 0;
          result.transfer += Number(sale.amount_transfer) || 0;
          result.qr += Number(sale.amount_qr) || 0;
        } else {
          result[sale.payment_method] += saleTotal;
        }
        result.total += saleTotal;

        // Una venta se factura por UN solo camino, así que no hay doble conteo.
        if (sale.fiscal_comprobante) {
          result.facturadoFiscal += saleTotal;
          result.cantFiscal += 1;
        } else if (sale.cae) {
          result.facturadoElectronica += saleTotal;
          result.cantElectronica += 1;
        }
      }
    });
    result.facturado = result.facturadoFiscal + result.facturadoElectronica;
    return result;
  }, [sales]);

  // Fila de producto (vista lista compacta: nombre | precio | stock)
  const ProductRow = ({ product, onAdd }: { product: Product; onAdd: () => void }) => {
    // Fraccionado: no lleva stock propio; el stock real es el del padre (granel),
    // que se muestra en kg porque es el recurso compartido entre todos los tamaños.
    const isFrac = !!product.source_product_id;
    const parentGrams = Number(product.source_current_stock ?? 0);
    const stockBase = isFrac ? parentGrams : Number(product.current_stock ?? 0);
    const noStock = stockBase <= 0;
    const dispUnit: Unit = (product.display_unit as Unit) || 'unit';

    return (
      <button
        onClick={onAdd}
        className={`w-full grid grid-cols-[1fr_auto_auto] items-center gap-2 sm:gap-4 px-3 sm:px-4 py-3 border-b border-cream-100 text-left transition-colors ${
          noStock
            ? 'bg-cream-50/50 hover:bg-cream-100/60'
            : 'bg-white hover:bg-cream-50'
        }`}
      >
        {/* Nombre + (stock/código en la sub-línea) */}
        <div className="min-w-0">
          <p className="font-medium text-taupe-800 flex items-start gap-1.5" title={product.name?.trim()}>
            {product.is_combo && (
              <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 mt-0.5">
                COMBO
              </span>
            )}
            {isFrac && (
              <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 mt-0.5 whitespace-nowrap">
                {product.fraction_grams ? `${Number(product.fraction_grams)} g` : 'FRACC.'}
              </span>
            )}
            <span className="break-words leading-tight">
              {product.name?.trim()}
              {isFrac && product.fraction_grams ? (
                <span className="text-taupe-500 font-normal"> · {Number(product.fraction_grams)} g</span>
              ) : null}
            </span>
          </p>
          <p className="text-xs truncate flex items-center gap-2">
            <span className={noStock ? 'text-rose-600 font-medium flex-shrink-0' : 'text-taupe-500 flex-shrink-0'}>
              {noStock ? 'Sin stock' : isFrac ? `${formatARNumber(parentGrams / 1000)} kg` : formatStock(stockBase, dispUnit)}
            </span>
            {product.is_combo && product.combo_items && product.combo_items.length > 0 ? (
              <span className="text-taupe-400 truncate" title={comboContentsLabel(product.combo_items)}>
                · Incluye: {comboContentsLabel(product.combo_items)}
              </span>
            ) : isFrac && product.source_product_name ? (
              <span className="text-taupe-400 truncate">· de {product.source_product_name}</span>
            ) : product.code ? (
              <span className="text-taupe-400 truncate">· {product.code}</span>
            ) : null}
          </p>
        </div>

        {/* Precio */}
        <p className="font-semibold text-emerald-700 tabular-nums whitespace-nowrap text-sm sm:text-base">
          {product.sale_price != null ? formatPricePerUnit(product.sale_price, dispUnit, false) : 'Variable'}
        </p>

        {/* Acción */}
        <div className="w-8 h-8 rounded-full bg-taupe-100 hover:bg-taupe-200 flex items-center justify-center flex-shrink-0 transition-colors">
          <Plus className="h-4 w-4 text-taupe-700" />
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-taupe-800">Ventas</h1>
        <p className="text-taupe-500">Gestión de ventas</p>
      </div>

      {/* Banner Caja Cerrada */}
      {isCashClosed && (
        <Link
          to="/dashboard/cash-register"
          className="block bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-5 shadow-lg hover:shadow-xl transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Caja cerrada</h3>
                <p className="text-amber-100 text-sm">Debes abrir la caja antes de registrar ventas</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg group-hover:bg-white/30 transition-all">
              <span className="text-white font-medium">Abrir Caja</span>
              <ArrowRight className="h-4 w-4 text-white group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
      )}

      {/* Tabs */}
      <div className="flex gap-3">
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-4 px-6 rounded-xl font-medium transition-all ${
            activeTab === 'history'
              ? 'bg-taupe-700 text-white shadow-lg'
              : 'bg-white border-2 border-cream-200 text-taupe-600 hover:border-taupe-300'
          }`}
        >
          <Eye className="h-5 w-5 inline mr-2" />
          Historial
        </button>
        <button
          onClick={() => setActiveTab('new')}
          className={`flex-1 py-4 px-6 rounded-xl font-medium transition-all ${
            activeTab === 'new'
              ? 'bg-green-600 text-white shadow-lg'
              : 'bg-white border-2 border-cream-200 text-taupe-600 hover:border-green-300'
          }`}
        >
          <Plus className="h-5 w-5 inline mr-2" />
          Nueva Venta
        </button>
      </div>

      {/* HISTORIAL */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-2xl border border-cream-200 p-6">
          {/* Filtros rápidos */}
          <div className="flex gap-2 mb-4">
            {(() => {
              const formatLocalDate = (date: Date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              const todayDate = new Date();
              const today = formatLocalDate(todayDate);
              const weekAgo = formatLocalDate(new Date(todayDate.getTime() - 7 * 24 * 60 * 60 * 1000));
              const monthStart = formatLocalDate(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));

              const isToday = dateFrom === today && dateTo === today;
              const isWeek = dateFrom === weekAgo && dateTo === today;
              const isMonth = dateFrom === monthStart && dateTo === today;
              const isAll = !dateFrom && !dateTo;

              return (
                <>
                  <button
                    onClick={() => { setDateFrom(today); setDateTo(today); }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${isToday ? 'bg-taupe-600 text-white' : 'bg-taupe-100 text-taupe-700 hover:bg-taupe-200'}`}
                  >
                    Hoy
                  </button>
                  {(
                    <>
                      <button
                        onClick={() => { setDateFrom(weekAgo); setDateTo(today); }}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${isWeek ? 'bg-taupe-600 text-white' : 'bg-taupe-100 text-taupe-700 hover:bg-taupe-200'}`}
                      >
                        Última semana
                      </button>
                      <button
                        onClick={() => { setDateFrom(monthStart); setDateTo(today); }}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${isMonth ? 'bg-taupe-600 text-white' : 'bg-taupe-100 text-taupe-700 hover:bg-taupe-200'}`}
                      >
                        Este mes
                      </button>
                      <button
                        onClick={() => { setPaymentFilter(''); setDateFrom(''); setDateTo(''); }}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${isAll ? 'bg-taupe-600 text-white' : 'bg-taupe-100 text-taupe-700 hover:bg-taupe-200'}`}
                      >
                        Todo
                      </button>
                    </>
                  )}
                </>
              );
            })()}
          </div>

          {/* Filtros avanzados */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Select
              label="Método de pago"
              options={PAYMENT_METHOD_OPTIONS}
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value as PaymentMethod | '')}
            />
            {(
              <>
                <Input type="date" label="Desde" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                <Input type="date" label="Hasta" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                <div className="flex items-end">
                  <Button
                    variant="secondary"
                    onClick={() => { setPaymentFilter(''); setDateFrom(''); setDateTo(''); }}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Limpiar
                  </Button>
                </div>
              </>
            )}

          </div>

          {/* Resumen. Al empleado se le muestran los totales solo cuando está
              mirando el día de hoy: los de días anteriores no los ve. */}
          {(!montosSoloHoy || rangoEsHoy) && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <div className="flex items-center gap-2 text-green-700 mb-1">
                  <Banknote className="h-5 w-5" />
                  <span className="font-medium">Efectivo</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold break-words text-green-800">{formatCurrency(totals.cash)}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-2 text-blue-700 mb-1">
                  <CreditCard className="h-5 w-5" />
                  <span className="font-medium">Tarjeta</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold break-words text-blue-800">{formatCurrency(totals.card)}</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                <div className="flex items-center gap-2 text-purple-700 mb-1">
                  <ArrowRightLeft className="h-5 w-5" />
                  <span className="font-medium">Transf.</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold break-words text-purple-800">{formatCurrency(totals.transfer)}</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                <div className="flex items-center gap-2 text-orange-700 mb-1">
                  <QrCode className="h-5 w-5" />
                  <span className="font-medium">QR</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold break-words text-orange-800">{formatCurrency(totals.qr)}</p>
              </div>
              <div className="bg-taupe-50 rounded-xl p-4 border border-taupe-200">
                <div className="flex items-center gap-2 text-taupe-700 mb-1">
                  <DollarSign className="h-5 w-5" />
                  <span className="font-medium">Total</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold break-words text-taupe-800">{formatCurrency(totals.total)}</p>
              </div>

              {/* Facturado: lo declarado ante ARCA por cualquiera de los dos caminos.
                  Solo tiene sentido mostrarlo si el local factura. */}
              {(tenant?.fiscal_enabled || tenant?.electronic_invoice_enabled) && (
                <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 col-span-2 md:col-span-3 lg:col-span-2">
                  <div className="flex items-center gap-2 text-indigo-700 mb-1">
                    <Receipt className="h-5 w-5" />
                    <span className="font-medium">Facturado</span>
                  </div>
                  <p className="text-lg sm:text-2xl font-bold break-words text-indigo-900">
                    {formatCurrency(totals.facturado)}
                  </p>
                  <div className="mt-1 text-xs text-indigo-700/80 space-y-0.5">
                    {tenant?.fiscal_enabled && (
                      <p>Hasar: {formatCurrency(totals.facturadoFiscal)} ({totals.cantFiscal})</p>
                    )}
                    {tenant?.electronic_invoice_enabled && (
                      <p>Electrónica: {formatCurrency(totals.facturadoElectronica)} ({totals.cantElectronica})</p>
                    )}
                    <p className="text-indigo-600/70">
                      Sin facturar: {formatCurrency(totals.total - totals.facturado)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tabla */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-taupe-600 mx-auto"></div>
              <p className="mt-3 text-taupe-500">Cargando ventas...</p>
            </div>
          ) : sales.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-16 w-16 text-taupe-300 mx-auto mb-4" />
              <p className="text-taupe-600 font-medium">No hay ventas</p>
              <p className="text-taupe-400 text-sm">Las ventas realizadas aparecerán aquí</p>
            </div>
          ) : (
            <>
            {/* Desktop: tabla */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-cream-200">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-taupe-600">Comprobante</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-taupe-600">Fecha</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-taupe-600">Cliente</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-taupe-600">Pago</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-taupe-600">Total</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-taupe-600">Estado</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-taupe-600">Ver</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-100">
                  {sales.map((sale) => (
                    <tr key={sale.id} className="hover:bg-cream-50">
                      <td className="px-4 py-3 font-mono text-sm">
                        <div className="flex flex-col gap-1 items-start">
                          <span>{sale.receipt_number || '-'}</span>
                          {sale.fiscal_comprobante && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold font-sans">
                              <Receipt className="h-3 w-3" /> Fiscal N° {sale.fiscal_comprobante}
                            </span>
                          )}
                          <FacturaEBadge sale={sale} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-taupe-600">{formatDate(sale.created_at)}</td>
                      <td className="px-4 py-3 text-sm">{sale.customer_name || 'Cliente ocasional'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm">
                          {sale.is_split ? (
                            <span className="px-2 py-0.5 rounded-full bg-taupe-100 text-taupe-700 text-xs font-medium">Mixto</span>
                          ) : (
                            <>
                              {PAYMENT_METHOD_ICONS[sale.payment_method]}
                              <span>{PAYMENT_METHOD_LABELS[sale.payment_method]}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-bold">
                        {formatCurrency(sale.total)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[sale.status]}`}>
                          {STATUS_LABELS[sale.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleViewDetail(sale)}
                          className="p-2 text-taupe-500 hover:text-taupe-700 hover:bg-taupe-100 rounded-lg"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        {sale.status === 'completed' && canCancelSale && (
                          <button
                            onClick={() => setCancelConfirm(sale)}
                            className="p-2 text-taupe-500 hover:text-red-600 hover:bg-red-50 rounded-lg ml-1"
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: tarjetas (para que no se desborde) */}
            <div className="md:hidden divide-y divide-cream-100">
              {sales.map((sale) => (
                <div key={sale.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-sm text-taupe-800">{sale.receipt_number || '-'}</p>
                    {sale.is_placeholder && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 text-[10px] font-semibold mt-0.5"
                        title="Comprobante entregado sin cobro. No suma a la caja, la ganancia ni los reportes."
                      >
                        <FileText className="h-3 w-3" /> Solo comprobante
                      </span>
                    )}
                    {sale.fiscal_comprobante && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold mt-0.5">
                        <Receipt className="h-3 w-3" /> Fiscal N° {sale.fiscal_comprobante}
                      </span>
                    )}
                    <FacturaEBadge sale={sale} className="mt-0.5" />
                    <p className="text-xs text-taupe-500 mt-0.5">{formatDate(sale.created_at)}</p>
                    <p className="text-sm text-taupe-700 truncate">{sale.customer_name || 'Cliente ocasional'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {sale.is_split ? (
                        <span className="px-2 py-0.5 rounded-full bg-taupe-100 text-taupe-700 text-xs font-medium">Mixto</span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-taupe-600">
                          {PAYMENT_METHOD_ICONS[sale.payment_method]} {PAYMENT_METHOD_LABELS[sale.payment_method]}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${STATUS_COLORS[sale.status]}`}>
                        {STATUS_LABELS[sale.status]}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-bold whitespace-nowrap ${sale.is_placeholder ? 'text-violet-400' : 'text-taupe-900'}`}>
                      {formatCurrency(sale.total)}
                    </p>
                    <div className="flex gap-1 justify-end mt-1">
                      <button
                        onClick={() => handleViewDetail(sale)}
                        className="p-2 text-taupe-500 hover:text-taupe-700 hover:bg-taupe-100 rounded-lg"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      {sale.status === 'completed' && canCancelSale && (
                        <button
                          onClick={() => setCancelConfirm(sale)}
                          className="p-2 text-taupe-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* NUEVA VENTA */}
      {activeTab === 'new' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 lg:gap-6">
          {/* Productos - ocupa más espacio */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-cream-200 p-3 sm:p-5">
            <h3 className="text-lg font-semibold text-taupe-800 mb-4 flex items-center gap-2">
              <LayoutGrid className="h-5 w-5 text-taupe-500" />
              Productos
            </h3>

            {/* Búsqueda */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-taupe-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o código..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-cream-200 rounded-xl text-base focus:outline-none focus:border-taupe-400"
              />
            </div>

            {/* Categorías y botón Otro */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                  categoryFilter === 'all'
                    ? 'bg-taupe-700 text-white'
                    : 'bg-cream-100 text-taupe-600 hover:bg-cream-200'
                }`}
              >
                Todos
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(String(cat.id))}
                  className={`px-4 py-2 rounded-xl font-medium transition-all ${
                    categoryFilter === String(cat.id)
                      ? 'bg-taupe-700 text-white'
                      : 'bg-cream-100 text-taupe-600 hover:bg-cream-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
              {tenant?.combos_enabled !== false && (
                <button
                  onClick={() => setShowComboBuilder(true)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all bg-purple-600 text-white hover:bg-purple-700 ml-auto"
                >
                  <Plus className="h-3.5 w-3.5 inline mr-1" />
                  Armar combo
                </button>
              )}
              {!showCustomItemForm ? (
                <button
                  onClick={() => setShowCustomItemForm(true)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200 ${tenant?.combos_enabled !== false ? '' : 'ml-auto'}`}
                >
                  <Plus className="h-3.5 w-3.5 inline mr-1" />
                  Otro
                </button>
              ) : (
                <div className="flex items-center gap-2 bg-purple-50 rounded-lg px-2 py-1 border border-purple-200">
                  <input
                    type="text"
                    placeholder="Nombre (ej. Viandas)"
                    value={customItemName}
                    onChange={(e) => setCustomItemName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustomItem(); }}
                    className="w-56 px-2 py-1 text-sm border border-purple-200 rounded focus:outline-none focus:border-purple-400 bg-white"
                    autoFocus
                  />
                  <input
                    type="number"
                    placeholder="$"
                    min="0"
                    step="0.01"
                    value={customItemPrice}
                    onChange={(e) => setCustomItemPrice(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustomItem(); }}
                    className="w-28 px-2 py-1 text-sm border border-purple-200 rounded focus:outline-none focus:border-purple-400 bg-white"
                  />
                  <button
                    onClick={handleAddCustomItem}
                    disabled={!customItemName.trim() || !customItemPrice}
                    className="p-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Agregar"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { setShowCustomItemForm(false); setCustomItemName(''); setCustomItemPrice(''); }}
                    className="p-1 text-purple-400 hover:text-purple-600"
                    title="Cancelar"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Grid de productos */}
            <div className="h-[calc(100vh-215px)] min-h-[520px] overflow-y-auto pr-2">
              {isLoadingProducts ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-taupe-600"></div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-taupe-400">
                  <Package className="h-16 w-16 mb-3" />
                  <p className="font-medium">No hay productos</p>
                </div>
              ) : (
                <div className="border border-cream-200 rounded-xl overflow-hidden">
                  {/* Header de columnas */}
                  <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 sm:gap-4 px-3 sm:px-4 py-2 bg-cream-100 border-b border-cream-200 text-xs uppercase tracking-wide font-semibold text-taupe-600">
                    <span>Producto <span className="normal-case text-taupe-400">· stock debajo del nombre</span></span>
                    <span className="text-right">Precio</span>
                    <span className="w-8"></span>
                  </div>
                  {/* Filas */}
                  {filteredProducts.map((product) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      onAdd={() => handleAddProduct(product)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Carrito */}
          <div className="lg:col-span-2 bg-gradient-to-b from-cream-50 to-cream-100 rounded-2xl border border-cream-200 p-3 sm:p-5 flex flex-col">
            <h3 className="text-lg font-semibold text-taupe-800 mb-4 flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-green-600" />
              Carrito
              {newSaleItems.length > 0 && (
                <span className="ml-auto bg-green-600 text-white text-sm px-3 py-1 rounded-full">
                  {newSaleItems.length}
                </span>
              )}
            </h3>

            {/* Items del carrito */}
            <div className="flex-1 overflow-y-auto mb-4">
              {newSaleItems.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-taupe-400 py-8">
                  <ShoppingBag className="h-16 w-16 mb-3 opacity-50" />
                  <p className="font-medium">Carrito vacío</p>
                  <p className="text-sm">Selecciona productos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {newSaleItems.map((item) => {
                    if (!item.product) {
                      const price = item.customPrice ?? 0;
                      return (
                        <div key={item.id} className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">Otro</span>
                              <p className="font-medium text-taupe-800">{item.customName}</p>
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="p-1 text-taupe-400 hover:text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="text-sm text-taupe-500 mb-1.5">{formatCurrency(price)} c/u</p>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                className="w-8 h-8 rounded-lg bg-purple-100 hover:bg-purple-200 font-bold flex-shrink-0"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={item.quantity}
                                onChange={(e) => handleUpdateQuantity(item.id, parseFloat(e.target.value) || 0)}
                                onFocus={(e) => e.target.select()}
                                className="w-12 text-center font-bold border border-purple-200 rounded-lg py-1 bg-white"
                              />
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                className="w-8 h-8 rounded-lg bg-purple-100 hover:bg-purple-200 font-bold flex-shrink-0"
                              >
                                +
                              </button>
                            </div>
                            <p className="font-bold text-green-600 text-lg whitespace-nowrap">
                              {formatCurrency(price * item.quantity)}
                            </p>
                          </div>
                        </div>
                      );
                    }

                    // El precio por display unit que ve el usuario
                    const productDimension: Dimension = (item.product.dimension as Dimension) || 'count';
                    const isWeight = productDimension === 'weight';
                    const stepVal = isWeight ? '0.01' : '1';
                    const unitOpts = compatibleUnits(productDimension);
                    // Venta por caja (bulto)
                    const canPack = !!item.product.pack_price && Number(item.product.units_per_pack ?? 1) > 1;
                    const packMode = isPackSale(item);
                    const packUnits = packUnitsOf(item);
                    // Precio "por unidad mostrada" (o por caja en modo pack)
                    const priceDisplay = packMode
                      ? Number(item.product.pack_price)
                      : item.customPrice != null
                        ? item.customPrice
                        : item.product.sale_price != null
                          ? pricePerDisplayFromBase(item.product.sale_price, item.quantity_unit)
                          : 0;
                    const subtotalCash = priceDisplay * item.quantity;
                    const needsPrice = !packMode && item.product.sale_price == null && item.customPrice == null;
                    const priceEditable = !packMode && item.product.sale_price == null;
                    // Verificar stock (en base unit). En caja: cajas × unidades.
                    const qtyBase = baseQtyForItem(item);
                    const stockBase = Number(item.product.current_stock ?? 0);
                    // Fraccionado: consume gramos del padre. Gramos necesarios = paquetes × gramos.
                    const isFracItem = !!item.product.source_product_id;
                    const fracGramsNeeded = qtyBase * Number(item.product.fraction_grams ?? 0);
                    const parentGrams = Number(item.product.source_current_stock ?? 0);
                    const stockExceeded = isFracItem
                      ? fracGramsNeeded > parentGrams
                      : item.product.track_stock && qtyBase > stockBase;

                    return (
                      <div key={item.id} className={`bg-white rounded-xl p-4 border ${item.comboId ? 'border-purple-300' : needsPrice ? 'border-orange-300' : 'border-cream-200'}`}>
                        {item.comboId && (
                          <div className="flex items-center gap-1 mb-1">
                            <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">COMBO</span>
                            <span className="text-xs text-purple-600 truncate">{item.comboLabel}</span>
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="font-medium text-taupe-800">
                            {item.product.name}
                            {isFracItem && item.product.fraction_grams ? (
                              <span className="text-taupe-500 font-normal"> · {Number(item.product.fraction_grams)} g</span>
                            ) : null}
                          </p>
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            title={item.comboId ? 'Quitar combo completo' : 'Quitar'}
                            className="p-1 text-taupe-400 hover:text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {canPack && (
                          <div className="inline-flex p-0.5 bg-cream-100 rounded-lg mb-2">
                            <button
                              type="button"
                              onClick={() => handleSetSaleMode(item.id, 'unit')}
                              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${!packMode ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500'}`}
                            >
                              Unidad
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSetSaleMode(item.id, 'pack')}
                              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${packMode ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500'}`}
                            >
                              Caja ({packUnits} u)
                            </button>
                          </div>
                        )}
                        <p className="text-sm text-taupe-500 mb-1.5">
                          {packMode
                            ? `${formatCurrency(priceDisplay)}/caja`
                            : `${formatCurrency(priceDisplay)}/${UNIT_LABEL_SHORT[item.quantity_unit]}`}
                        </p>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleUpdateQuantity(item.id, isWeight ? Math.max(0, item.quantity - 0.1) : item.quantity - 1)}
                              className="w-8 h-8 rounded-lg bg-cream-100 hover:bg-cream-200 font-bold flex-shrink-0"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="0"
                              step={stepVal}
                              value={item.quantity}
                              onChange={(e) => handleUpdateQuantity(item.id, parseFloat(e.target.value) || 0)}
                              onFocus={(e) => e.target.select()}
                              className="w-12 text-center font-bold border border-cream-200 rounded-lg py-1"
                            />
                            <button
                              onClick={() => handleUpdateQuantity(item.id, isWeight ? item.quantity + 0.1 : item.quantity + 1)}
                              className="w-8 h-8 rounded-lg bg-cream-100 hover:bg-cream-200 font-bold flex-shrink-0"
                            >
                              +
                            </button>
                            {!packMode && unitOpts.length > 1 && (
                              <select
                                value={item.quantity_unit}
                                onChange={(e) => handleUpdateQuantityUnit(item.id, e.target.value as Unit)}
                                className="text-sm border border-cream-200 rounded-lg py-1 px-1.5 bg-white"
                              >
                                {unitOpts.map((u) => (
                                  <option key={u} value={u}>{UNIT_LABEL_SHORT[u]}</option>
                                ))}
                              </select>
                            )}
                          </div>
                          <p className="font-bold text-green-600 text-lg whitespace-nowrap">
                            {formatCurrency(subtotalCash)}
                          </p>
                        </div>

                        {packMode && (
                          <p className="mt-1 text-xs text-taupe-500">
                            {item.quantity} caja{item.quantity !== 1 ? 's' : ''} = <strong>{qtyBase}</strong> unidades (descuenta del stock)
                          </p>
                        )}

                        {stockExceeded && (
                          <p className="mt-2 text-xs text-orange-600">
                            {isFracItem
                              ? `Stock del padre: ${formatARNumber(parentGrams / 1000)} kg (estás pidiendo ${formatARNumber(fracGramsNeeded / 1000)} kg)`
                              : `Stock disponible: ${formatStock(stockBase, item.quantity_unit)}`}
                          </p>
                        )}

                        {priceEditable && (
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-sm text-taupe-500">Precio/{UNIT_LABEL_SHORT[item.quantity_unit]}:</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0"
                              value={item.customPrice ?? ''}
                              onChange={(e) => handleUpdatePrice(item.id, e.target.value ? parseFloat(e.target.value) : undefined)}
                              className="w-28 px-3 py-1 border rounded-lg border-orange-400 bg-orange-50"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Datos del cliente */}
            <div className="space-y-2 mb-4">
              <label className="block text-sm font-medium text-taupe-700">Datos del cliente (opcional)</label>
              <input
                type="text"
                placeholder="Nombre"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full px-3 py-2 border border-cream-300 rounded-xl text-sm focus:outline-none focus:border-taupe-400"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Teléfono"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-cream-300 rounded-xl text-sm focus:outline-none focus:border-taupe-400"
                />
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    placeholder="DNI (para puntos VIP)"
                    value={customerDocument}
                    onChange={(e) => setCustomerDocument(e.target.value)}
                    className="flex-1 min-w-0 px-3 py-2 border border-cream-300 rounded-xl text-sm focus:outline-none focus:border-taupe-400"
                  />
                  <button
                    type="button"
                    onClick={handleConsultarPuntos}
                    disabled={consultingPoints || customerDocument.trim().length < 6}
                    title="Consultar puntos del cliente"
                    className="px-2.5 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-40 flex items-center gap-1 flex-shrink-0"
                  >
                    {consultingPoints ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
                    <span className="text-xs font-medium hidden sm:inline">Consultar</span>
                  </button>
                </div>
              </div>

              {/* Cliente VIP (puntos) */}
              {customerDocument.trim().length >= 6 && (
                vipLoading ? (
                  <div className="flex items-center gap-2 text-sm text-taupe-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Buscando cliente VIP…
                  </div>
                ) : vipCustomer ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 min-w-0">
                        <Star className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        <span className="text-sm font-semibold text-amber-800 truncate">
                          {vipCustomer.name || `DNI ${vipCustomer.dni}`}
                        </span>
                      </span>
                      <span className="text-sm font-bold text-amber-700 whitespace-nowrap">{vipCustomer.points_balance} pts</span>
                    </div>
                    {canRedeemVip ? (
                      <label className={`flex items-center gap-2 text-sm cursor-pointer ${splitMode ? 'opacity-50' : 'text-amber-800'}`}>
                        <input
                          type="checkbox"
                          checked={vipRedeem && !splitMode}
                          onChange={(e) => setVipRedeem(e.target.checked)}
                          disabled={splitMode}
                        />
                        Canjear {LOYALTY_REDEEM_POINTS} pts (−{formatCurrency(LOYALTY_REDEEM_VALUE)})
                        {splitMode && <span className="text-xs">no aplica en pago dividido</span>}
                      </label>
                    ) : (
                      <p className="text-xs text-amber-600">
                        Le faltan {LOYALTY_REDEEM_POINTS - vipCustomer.points_balance} pts para canjear {formatCurrency(LOYALTY_REDEEM_VALUE)}.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-taupe-400 flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5" /> Cliente nuevo — sumará puntos VIP al cobrar.
                  </p>
                )
              )}
            </div>

            {/* Método de pago */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-taupe-700">Método de pago</label>
                <button
                  type="button"
                  onClick={() => setSplitMode((v) => !v)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${splitMode ? 'bg-taupe-700 border-taupe-700 text-white' : 'bg-white border-cream-200 text-taupe-600 hover:border-taupe-300'}`}
                >
                  Pago dividido
                </button>
              </div>

              {!splitMode ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(['cash', 'card', 'transfer', 'qr'] as PaymentMethod[]).map((method) => (
                    <button
                      key={method}
                      onClick={() => setNewSalePayment(method)}
                      className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                        newSalePayment === method
                          ? 'bg-taupe-700 border-taupe-700 text-white'
                          : 'bg-white border-cream-200 text-taupe-600 hover:border-taupe-300'
                      }`}
                    >
                      {PAYMENT_METHOD_ICONS[method]}
                      <span className="text-xs font-medium">{PAYMENT_METHOD_LABELS[method]}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-3 bg-white rounded-xl border-2 border-cream-200 p-3">
                  <p className="text-xs text-taupe-500">
                    Ingresá cuánto cobrás por cada medio y, si querés, un <strong>% de descuento</strong> a ese medio —
                    el sistema calcula el neto.
                    <br />Subtotal de referencia: <strong>{formatCurrency(newSaleTotal)}</strong>
                  </p>
                  {([
                    ['cash', splitCash, setSplitCash, splitCashDisc, setSplitCashDisc],
                    ['card', splitCard, setSplitCard, splitCardDisc, setSplitCardDisc],
                    ['transfer', splitTransfer, setSplitTransfer, splitTransferDisc, setSplitTransferDisc],
                    ['qr', splitQr, setSplitQr, splitQrDisc, setSplitQrDisc],
                  ] as [PaymentMethod, string, (v: string) => void, string, (v: string) => void][]).map(([method, val, setter, disc, discSetter]) => {
                    const gross = splitGross(val);
                    const net = splitNet(val, disc);
                    const hasDisc = gross > 0 && (parseFloat(disc) || 0) > 0;
                    return (
                      <div key={method}>
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1.5 w-24 flex-shrink-0 text-sm text-taupe-700">
                            {PAYMENT_METHOD_ICONS[method]} {PAYMENT_METHOD_LABELS[method]}
                          </span>
                          <CurrencyInput value={val} onChange={setter} placeholder="0" wrapperClassName="flex-1" />
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="0"
                              value={disc}
                              onChange={(e) => discSetter(e.target.value)}
                              className="w-12 border border-cream-200 rounded-lg px-1.5 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-taupe-400"
                            />
                            <span className="text-xs text-taupe-400">%</span>
                          </div>
                        </div>
                        {hasDisc && (
                          <div className="text-right text-xs text-green-600 mt-0.5">
                            neto: <strong>{formatCurrency(net)}</strong> <span className="text-taupe-400">(−{formatCurrency(gross - net)})</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {newSaleTotal - splitSum > 0.009 && (
                    <div className="flex justify-between text-xs text-green-600 font-medium pt-1">
                      <span>Descuento total (subtotal − cobrado):</span>
                      <span>-{formatCurrency(newSaleTotal - splitSum)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-semibold text-taupe-800 border-t border-cream-100 pt-2">
                    <span>Total cobrado:</span>
                    <span>{formatCurrency(splitSum)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Total y botón */}
            <div className="bg-white rounded-xl p-4 border-2 border-green-200 space-y-3">
              {/* Descuento (no aplica en pago dividido: ahí se ingresa a mano) */}
              {!splitMode && (
                <div className="border rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium text-gray-600">Descuento (opcional)</p>
                  <div className="flex gap-2">
                    <button
                      className={`flex-1 py-1.5 text-sm rounded-lg border font-medium transition-colors ${directDiscountType === 'percentage' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-white border-gray-200 text-gray-500'}`}
                      onClick={() => { setDirectDiscountType('percentage'); setDirectDiscountValue(''); }}
                    >% Porcentaje</button>
                    <button
                      className={`flex-1 py-1.5 text-sm rounded-lg border font-medium transition-colors ${directDiscountType === 'fixed' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-white border-gray-200 text-gray-500'}`}
                      onClick={() => { setDirectDiscountType('fixed'); setDirectDiscountValue(''); }}
                    >$ Monto fijo</button>
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder={directDiscountType === 'percentage' ? 'Ej: 10 (10%)' : 'Ej: 5000'}
                    value={directDiscountValue}
                    onChange={(e) => setDirectDiscountValue(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                  />

                  {/* El motivo aparece recien cuando hay descuento: si estuviera
                      siempre, seria un campo mas que se ignora. */}
                  {directDiscountNum > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-600">
                          Motivo del descuento
                        </label>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => setShowReasonsModal(true)}
                            className="text-xs text-taupe-600 hover:text-taupe-800 underline underline-offset-2"
                          >
                            Administrar
                          </button>
                        )}
                      </div>
                      <select
                        value={discountReasonId}
                        onChange={(e) => setDiscountReasonId(e.target.value ? Number(e.target.value) : '')}
                        className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                          discountReasonId === ''
                            ? 'border-orange-300 focus:ring-orange-300 bg-orange-50'
                            : 'border-gray-200 focus:ring-green-300'
                        }`}
                      >
                        <option value="">Elegí un motivo…</option>
                        {discountReasons.map((m) => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                      {discountReasonId === '' && (
                        <p className="text-xs text-orange-600 mt-1">
                          Hace falta para poder cobrar con descuento.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-1">
                {!splitMode && directDiscountNum > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-400">
                    <span>Subtotal:</span>
                    <span className="line-through">{formatCurrency(newSaleTotal)}</span>
                  </div>
                )}
                {!splitMode && directDiscountNum > 0 && (
                  <div className="flex justify-between items-center text-sm text-green-600 font-medium">
                    <span>Descuento:</span>
                    <span>-{directDiscountType === 'percentage' ? `${directDiscountNum}%` : formatCurrency(directDiscountNum)}</span>
                  </div>
                )}
                {loyaltyDiscount > 0 && (
                  <div className="flex justify-between items-center text-sm text-amber-600 font-medium">
                    <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5" /> Canje puntos ({LOYALTY_REDEEM_POINTS} pts):</span>
                    <span>-{formatCurrency(loyaltyDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-taupe-600 font-medium">Total:</span>
                  <span className={`text-3xl font-bold ${placeholderMode ? 'text-violet-600' : 'text-green-600'}`}>
                    {formatCurrency(effectiveTotal)}
                  </span>
                </div>
              </div>

              {hasItemsWithoutPrice && (
                <p className="text-sm text-orange-600 text-center">
                  Ingrese el precio de los servicios
                </p>
              )}
              {splitMode && splitSum <= 0 && (
                <p className="text-sm text-orange-600 text-center">
                  Ingresá los montos del pago dividido
                </p>
              )}
              {/* Comprobante sin venta. Va en el panel y no en el modal de cobro porque
                  el flujo real es: primero el ticket oficial de la mercaderia, despues
                  este. Conviene verlo mientras se arma, no recordarlo al final. */}
              <label
                className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                  placeholderMode
                    ? 'bg-violet-50 border-violet-300'
                    : 'bg-white border-cream-200 hover:border-cream-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={placeholderMode}
                  onChange={(e) => setPlaceholderMode(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-taupe-800">
                    Solo comprobante — no cobra
                  </span>
                  <span className="block text-xs text-taupe-500 mt-0.5">
                    Imprime el ticket para entregar, pero no suma a la caja, ni a la ganancia,
                    ni a los reportes, y no da puntos. Para cuando el cobro se hace por otra vía.
                  </span>
                </span>
              </label>

              <Button
                className={`w-full py-4 text-lg font-bold rounded-xl ${
                  placeholderMode
                    ? 'bg-violet-600 hover:bg-violet-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                disabled={newSaleItems.length === 0 || createSale.isPending || hasItemsWithoutPrice || (splitMode && splitSum <= 0)}
                onClick={() => setConfirmCheckout(true)}
              >
                {placeholderMode
                  ? <FileText className="h-5 w-5 mr-2" />
                  : <Check className="h-5 w-5 mr-2" />}
                {createSale.isPending
                  ? 'Procesando...'
                  : placeholderMode ? 'Generar comprobante' : 'Cobrar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalle de venta */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => { setIsDetailModalOpen(false); setSelectedSale(null); }}
        title={`Venta ${saleDetail?.receipt_number || ''}`}
      >
        {saleDetail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-taupe-500">Fecha</p>
                <p className="font-medium">{formatDate(saleDetail.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-taupe-500">Tipo</p>
                <p className="font-medium">{TYPE_LABELS[saleDetail.sale_type]}</p>
              </div>
              <div>
                <p className="text-sm text-taupe-500">Cliente</p>
                <p className="font-medium">{saleDetail.customer_name || 'Cliente ocasional'}</p>
              </div>
              <div>
                <p className="text-sm text-taupe-500">Pago</p>
                {saleDetail.is_split ? (
                  <div className="space-y-0.5">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-taupe-100 text-taupe-700 text-xs font-medium mb-1">Pago dividido</span>
                    {(Number(saleDetail.amount_cash) || 0) > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-taupe-600">Efectivo</span><span className="font-medium">{formatCurrency(saleDetail.amount_cash)}</span></div>
                    )}
                    {(Number(saleDetail.amount_card) || 0) > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-taupe-600">Tarjeta</span><span className="font-medium">{formatCurrency(saleDetail.amount_card)}</span></div>
                    )}
                    {(Number(saleDetail.amount_transfer) || 0) > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-taupe-600">Transferencia</span><span className="font-medium">{formatCurrency(saleDetail.amount_transfer)}</span></div>
                    )}
                    {(Number(saleDetail.amount_qr) || 0) > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-taupe-600">QR</span><span className="font-medium">{formatCurrency(saleDetail.amount_qr)}</span></div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {PAYMENT_METHOD_ICONS[saleDetail.payment_method]}
                    <span className="font-medium">{PAYMENT_METHOD_LABELS[saleDetail.payment_method]}</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <p className="text-sm text-taupe-500 mb-2">Items</p>
              <div className="border rounded-lg divide-y">
                {saleDetail.items.map((item) => (
                  <div key={item.id} className="p-3 flex justify-between">
                    <div>
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-taupe-500">
                        {item.quantity} x {formatCurrency(item.unit_price)}
                      </p>
                    </div>
                    <p className="font-medium">{formatCurrency(item.subtotal)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-4 space-y-1">
              {Number(saleDetail.subtotal) > Number(saleDetail.total) && (
                <>
                  <div className="flex justify-between text-sm text-taupe-500">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(saleDetail.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600 font-medium">
                    <span>Descuento{Number(saleDetail.discount_percentage) > 0 ? ` (${Number(saleDetail.discount_percentage)}%)` : ''}:</span>
                    <span>-{formatCurrency(Number(saleDetail.subtotal) - Number(saleDetail.total))}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span>{formatCurrency(saleDetail.total)}</span>
              </div>
            </div>

            {/* Puntos VIP: ya acreditados, o permitir agregar el DNI si falta */}
            {saleDetail.status !== 'cancelled' && (
              (saleDetail.points_earned && saleDetail.points_earned > 0) ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  Sumó {saleDetail.points_earned} puntos VIP
                  {saleDetail.customer_document ? ` · DNI ${saleDetail.customer_document}` : ''}
                </div>
              ) : (Number(saleDetail.total) >= LOYALTY_MIN_AMOUNT) ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                  <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5">
                    <Star className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    Sin DNI cargado — esta venta puede sumar {pointsForAmount(Number(saleDetail.total))} puntos
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="DNI del cliente"
                      value={ojitoDni}
                      onChange={(e) => setOjitoDni(e.target.value)}
                      className="flex-1 px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white focus:outline-none focus:border-amber-400"
                    />
                    <Button onClick={handleAssignDni} disabled={!ojitoDni.trim() || ojitoDniLoading}>
                      {ojitoDniLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sumar puntos'}
                    </Button>
                  </div>
                </div>
              ) : null
            )}

            {/* Acciones de impresión */}
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wide text-taupe-400 font-semibold">Imprimir / Facturar</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {/* Ticket común */}
                <button
                  type="button"
                  onClick={() => printSaleTicket(saleDetail, { name: tenant?.name, cuit: tenant?.cuit, address: tenant?.address })}
                  className="group flex items-center gap-3 rounded-xl border-2 border-cream-200 bg-white hover:border-taupe-400 hover:bg-cream-50 p-3 text-left transition-colors"
                >
                  <span className="h-9 w-9 rounded-full bg-taupe-100 group-hover:bg-taupe-200 flex items-center justify-center flex-shrink-0">
                    <Printer className="h-4 w-4 text-taupe-700" />
                  </span>
                  <span>
                    <span className="block font-semibold text-taupe-800 text-sm">Ticket común</span>
                    <span className="block text-xs text-taupe-500">No fiscal · reimprimir</span>
                  </span>
                </button>

                {/* Comprobante fiscal: reimprimir si ya se facturó, o facturar si no.
                    Se oculta si ya se facturó por ARCA (sería declararla dos veces). */}
                {tenant?.fiscal_enabled && !(saleDetail.cae && !saleDetail.fiscal_comprobante) && (
                  <button
                    type="button"
                    disabled={fiscalPrinting}
                    onClick={async () => {
                      if (saleDetail.cae && !saleDetail.fiscal_comprobante) {
                        toast.error('Esta venta ya tiene factura electrónica; no se puede facturar también por la Hasar');
                        return;
                      }
                      setFiscalPrinting(true);
                      try {
                        if (saleDetail.fiscal_comprobante) {
                          await reprintFiscal(saleDetail.fiscal_comprobante);
                          toast.success(`Reimpreso comprobante fiscal N° ${saleDetail.fiscal_comprobante}`);
                        } else {
                          const numero = await printFiscal(saleDetail, { cuit: tenant?.cuit });
                          if (numero) {
                            try {
                              await salesService.setFiscalComprobante(saleDetail.id, numero);
                              // Refrescar la vista para que aparezcan "Reimprimir" y "Nota de crédito"
                              await queryClient.invalidateQueries({ queryKey: ['sale', saleDetail.id] });
                              queryClient.invalidateQueries({ queryKey: ['sales'] });
                            } catch { /* no bloquear */ }
                          }
                          toast.success('Comprobante fiscal emitido');
                        }
                      } catch (e: any) {
                        toastFiscalError(e, 'No se pudo');
                      } finally {
                        setFiscalPrinting(false);
                      }
                    }}
                    className="group flex items-center gap-3 rounded-xl border-2 border-emerald-500 bg-emerald-600 hover:bg-emerald-700 p-3 text-left transition-colors text-white disabled:opacity-60"
                  >
                    <span className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                      {fiscalPrinting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                    </span>
                    <span>
                      <span className="block font-semibold text-sm">
                        {saleDetail.fiscal_comprobante ? 'Reimprimir fiscal' : 'Facturar (fiscal)'}
                      </span>
                      <span className="block text-xs text-emerald-50/90">
                        {saleDetail.fiscal_comprobante ? `Comprobante N° ${saleDetail.fiscal_comprobante}` : 'Emitir por la Hasar'}
                      </span>
                    </span>
                  </button>
                )}

                {/* Factura electrónica ARCA (WSFE). Se oculta si ya se facturó por la Hasar. */}
                {tenant?.electronic_invoice_enabled && !(saleDetail.fiscal_comprobante && !saleDetail.cae) && (
                  <button
                    type="button"
                    disabled={emittingElectronic}
                    onClick={() => handleEmitElectronic(saleDetail)}
                    className="group flex items-center gap-3 rounded-xl border-2 border-indigo-500 bg-indigo-600 hover:bg-indigo-700 p-3 text-left transition-colors text-white disabled:opacity-60"
                  >
                    <span className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                      {emittingElectronic ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                    </span>
                    <span>
                      <span className="block font-semibold text-sm">
                        {saleDetail.cae ? 'Reimprimir factura electrónica' : 'Factura electrónica (ARCA)'}
                      </span>
                      <span className="block text-xs text-indigo-50/90">
                        {saleDetail.cae
                          ? `Factura C N° ${String(saleDetail.e_pto_vta ?? '').padStart(4, '0')}-${String(saleDetail.e_cbte_nro ?? '').padStart(8, '0')}`
                          : 'Emitir Factura C por ARCA'}
                      </span>
                    </span>
                  </button>
                )}
              </div>

              {/* Nota de crédito: solo si esta venta ya se facturó fiscalmente */}
              {tenant?.fiscal_enabled && saleDetail.fiscal_comprobante && (
                <button
                  type="button"
                  disabled={fiscalPrinting || cancelSale.isPending}
                  onClick={() => setNcConfirm(saleDetail)}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 py-2.5 text-sm font-medium disabled:opacity-60"
                >
                  <XCircle className="h-4 w-4" />
                  Nota de crédito + cancelar venta (N° {saleDetail.fiscal_comprobante})
                </button>
              )}

              {/* Editar venta (cualquier rol, si no está cancelada — pide motivo) */}
              {saleDetail.status !== 'cancelled' && (
                <button
                  type="button"
                  onClick={() => openEditSale(saleDetail)}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border border-taupe-300 bg-white hover:bg-cream-50 text-taupe-700 py-2.5 text-sm font-medium"
                >
                  <Filter className="h-4 w-4" />
                  Editar medio de pago / descuento
                </button>
              )}

              <div className="flex gap-2.5 pt-1">
                <Button variant="secondary" className="flex-1" onClick={() => { if (tenant) generateSaleReceipt(saleDetail, tenant); }}>
                  <Download className="h-4 w-4 mr-2" />
                  PDF
                </Button>
                <Button variant="secondary" className="flex-1" onClick={() => setIsDetailModalOpen(false)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal editar venta (admin): medio de pago + descuento */}
      <Modal
        isOpen={!!editingSale}
        onClose={() => { if (!editSaving) setEditingSale(null); }}
        title={`Editar venta ${editingSale?.receipt_number || ''}`}
      >
        {editingSale && (() => {
          const editSubtotal = Number(editingSale.subtotal || 0);
          const editDiscNum = parseFloat(editDiscValue) || 0;
          const editNewTotal = editSplit
            ? (['cash', 'card', 'transfer', 'qr'] as PaymentMethod[]).reduce((s, k) => s + (parseFloat(editSplitAmt[k]) || 0), 0)
            : editDiscType === 'percentage'
              ? Math.max(0, editSubtotal * (1 - editDiscNum / 100))
              : Math.max(0, editSubtotal - editDiscNum);
          return (
            <div className="space-y-4">
              <p className="text-xs text-taupe-500">
                Se cambian solo el medio de pago y el descuento (los ítems no). Subtotal: <strong>{formatCurrency(editSubtotal)}</strong>
              </p>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-taupe-700">Método de pago</span>
                <button
                  type="button"
                  onClick={() => setEditSplit((v) => !v)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${editSplit ? 'bg-taupe-700 border-taupe-700 text-white' : 'bg-white border-cream-200 text-taupe-600 hover:border-taupe-300'}`}
                >
                  Pago dividido
                </button>
              </div>

              {!editSplit ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(['cash', 'card', 'transfer', 'qr'] as PaymentMethod[]).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setEditPayment(m)}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${editPayment === m ? 'bg-taupe-700 border-taupe-700 text-white' : 'bg-white border-cream-200 text-taupe-600 hover:border-taupe-300'}`}
                      >
                        {PAYMENT_METHOD_ICONS[m]}
                        <span className="text-xs font-medium">{PAYMENT_METHOD_LABELS[m]}</span>
                      </button>
                    ))}
                  </div>
                  <div className="border rounded-lg p-3 space-y-2">
                    <p className="text-sm font-medium text-gray-600">Descuento</p>
                    <div className="flex gap-2">
                      <button className={`flex-1 py-1.5 text-sm rounded-lg border font-medium ${editDiscType === 'percentage' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-white border-gray-200 text-gray-500'}`} onClick={() => { setEditDiscType('percentage'); setEditDiscValue(''); }}>% Porcentaje</button>
                      <button className={`flex-1 py-1.5 text-sm rounded-lg border font-medium ${editDiscType === 'fixed' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-white border-gray-200 text-gray-500'}`} onClick={() => { setEditDiscType('fixed'); setEditDiscValue(''); }}>$ Monto fijo</button>
                    </div>
                    <input
                      type="number"
                      min="0"
                      placeholder={editDiscType === 'percentage' ? 'Ej: 10 (10%)' : 'Ej: 5000'}
                      value={editDiscValue}
                      onChange={(e) => setEditDiscValue(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-2 border rounded-lg p-3">
                  <p className="text-xs text-taupe-500">Ingresá el monto neto cobrado por cada medio.</p>
                  {(['cash', 'card', 'transfer', 'qr'] as PaymentMethod[]).map((m) => (
                    <div key={m} className="flex items-center gap-2">
                      <span className="flex items-center gap-1.5 w-24 flex-shrink-0 text-sm text-taupe-700">{PAYMENT_METHOD_ICONS[m]} {PAYMENT_METHOD_LABELS[m]}</span>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={editSplitAmt[m]}
                        onChange={(e) => setEditSplitAmt((a) => ({ ...a, [m]: e.target.value }))}
                        className="flex-1 border border-cream-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-taupe-400"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-taupe-700 mb-1">Motivo del cambio <span className="text-red-500">*</span></label>
                <textarea
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  placeholder="Ej: el cliente pagó en efectivo y se le aplicó 10%"
                  rows={2}
                  className="w-full border border-cream-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400"
                />
                <p className="text-xs text-taupe-400 mt-0.5">Queda registrado en auditoría (quién y por qué).</p>
              </div>

              <div className="flex justify-between items-center border-t border-cream-100 pt-3">
                <span className="text-taupe-600 font-medium">Nuevo total:</span>
                <span className="text-2xl font-bold text-green-600">{formatCurrency(editNewTotal)}</span>
              </div>

              <div className="flex gap-3">
                <Button variant="secondary" className="flex-1" onClick={() => setEditingSale(null)} disabled={editSaving}>Cancelar</Button>
                <Button className="flex-1" onClick={handleSaveEdit} disabled={editSaving || !editReason.trim()}>
                  {editSaving ? 'Guardando…' : 'Guardar cambios'}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Modal de confirmación de cobro: muestra el detalle antes de cerrar la venta */}
      <Modal
        isOpen={confirmCheckout}
        onClose={() => { if (!createSale.isPending) setConfirmCheckout(false); }}
        title="Confirmar cobro"
        closeOnBackdropClick={!createSale.isPending}
      >
        <div className="space-y-4">
          {/* Ítems */}
          <div className="rounded-xl border border-cream-200 divide-y divide-cream-100 max-h-64 overflow-y-auto">
            {newSaleItems.map((item) => {
              const nombre = item.product?.name || item.customName || 'Producto';
              const lineTotal = priceForItem(item) * baseQtyForItem(item);
              const unidad = item.quantity_unit && item.quantity_unit !== 'unit'
                ? UNIT_LABEL_SHORT[item.quantity_unit] : '';
              return (
                <div key={item.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-taupe-800 truncate">{nombre}</p>
                    <p className="text-xs text-taupe-500">{item.quantity} {unidad}</p>
                  </div>
                  <span className="text-sm font-semibold text-taupe-800 whitespace-nowrap">{formatCurrency(lineTotal)}</span>
                </div>
              );
            })}
          </div>

          {/* Cliente (si se cargó) */}
          {(customerName.trim() || customerDocument.trim()) && (
            <div className="text-sm text-taupe-600">
              <span className="text-taupe-400">Cliente: </span>
              {customerName.trim() || 'Sin nombre'}{customerDocument.trim() ? ` · DNI ${customerDocument.trim()}` : ''}
            </div>
          )}

          {/* Recordatorio VIP: si la compra suma puntos, ofrecer cargar el DNI acá mismo */}
          {effectiveTotal >= LOYALTY_MIN_AMOUNT && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
              <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5">
                <Star className="h-4 w-4 text-amber-500 flex-shrink-0" />
                Esta compra suma {pointsForAmount(effectiveTotal)} puntos VIP
              </p>
              {!customerDocument.trim() ? (
                <>
                  <input
                    type="text"
                    placeholder="DNI del cliente (para acreditar los puntos)"
                    value={customerDocument}
                    onChange={(e) => setCustomerDocument(e.target.value)}
                    className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white focus:outline-none focus:border-amber-400"
                  />
                  <p className="text-xs text-amber-600">Sin DNI no se acreditan los puntos. Es opcional.</p>
                </>
              ) : vipLoading ? (
                <div className="flex items-center gap-2 text-sm text-amber-700">
                  <Loader2 className="h-4 w-4 animate-spin" /> Buscando cliente…
                </div>
              ) : vipCustomer ? (
                <p className="text-sm text-amber-800">
                  <strong>{vipCustomer.name || `DNI ${vipCustomer.dni}`}</strong> · saldo {vipCustomer.points_balance} pts → sumará {pointsForAmount(effectiveTotal)} pts
                </p>
              ) : (
                <p className="text-sm text-amber-800">
                  DNI {customerDocument.trim()} — cliente nuevo: se registra y suma {pointsForAmount(effectiveTotal)} pts.
                </p>
              )}
            </div>
          )}

          {/* Medio de pago / desglose */}
          {!splitMode ? (
            <div className="flex items-center gap-2 text-sm text-taupe-700">
              {PAYMENT_METHOD_ICONS[newSalePayment]}
              <span className="font-medium">{PAYMENT_METHOD_LABELS[newSalePayment]}</span>
            </div>
          ) : (
            <div className="rounded-xl bg-cream-50 border border-cream-200 p-3 space-y-1">
              <p className="text-xs uppercase tracking-wide text-taupe-400 font-semibold mb-1">Pago dividido</p>
              {([['cash', splitCashNum], ['card', splitCardNum], ['transfer', splitTransferNum], ['qr', splitQrNum]] as [PaymentMethod, number][])
                .filter(([, v]) => v > 0)
                .map(([method, v]) => (
                  <div key={method} className="flex justify-between text-sm text-taupe-700">
                    <span className="flex items-center gap-1.5">{PAYMENT_METHOD_ICONS[method]} {PAYMENT_METHOD_LABELS[method]}</span>
                    <span className="font-medium">{formatCurrency(v)}</span>
                  </div>
                ))}
            </div>
          )}

          {/* El modo se elige en el panel de venta; aca solo se confirma en que modo
              se esta, para que nadie cobre creyendo que emite un comprobante. */}
          {placeholderMode && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-violet-50 border border-violet-200">
              <FileText className="h-4 w-4 text-violet-600 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-violet-800">
                <strong>Solo comprobante.</strong> Se imprime el ticket para entregar, pero
                no entra plata: no suma a la caja, la ganancia ni los reportes, y no da puntos.
              </span>
            </div>
          )}

          {/* Totales */}
          <div className="border-t border-cream-100 pt-3 space-y-1">
            {!splitMode && directDiscountNum > 0 && (
              <>
                <div className="flex justify-between text-sm text-taupe-400">
                  <span>Subtotal:</span>
                  <span className="line-through">{formatCurrency(newSaleTotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-green-600 font-medium">
                  <span>Descuento:</span>
                  <span>-{directDiscountType === 'percentage' ? `${directDiscountNum}%` : formatCurrency(directDiscountNum)}</span>
                </div>
              </>
            )}
            {loyaltyDiscount > 0 && (
              <div className="flex justify-between text-sm text-amber-600 font-medium">
                <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5" /> Canje puntos ({LOYALTY_REDEEM_POINTS} pts):</span>
                <span>-{formatCurrency(loyaltyDiscount)}</span>
              </div>
            )}
            {vipCustomer && (
              <div className="flex justify-between text-xs text-amber-500">
                <span>Cliente VIP · saldo {vipCustomer.points_balance} pts</span>
                {loyaltyDiscount > 0 && <span>quedará {Math.max(0, vipCustomer.points_balance - LOYALTY_REDEEM_POINTS)} pts</span>}
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-taupe-700 font-semibold">
                {placeholderMode ? 'Total del comprobante:' : 'Total a cobrar:'}
              </span>
              <span className={`text-3xl font-bold ${placeholderMode ? 'text-violet-600' : 'text-green-600'}`}>
                {formatCurrency(effectiveTotal)}
              </span>
            </div>
          </div>

          {/* Acciones */}
          <div className="flex gap-3 pt-1">
            <Button variant="secondary" className="flex-1" onClick={() => setConfirmCheckout(false)} disabled={createSale.isPending}>
              Volver
            </Button>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={handleCreateSale}
              disabled={createSale.isPending}
            >
              <Check className="h-4 w-4 mr-2" />
              {createSale.isPending
                ? 'Procesando...'
                : placeholderMode ? 'Generar comprobante' : 'Confirmar cobro'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal post-venta: ofrecer imprimir el ticket */}
      <Modal
        isOpen={!!printPrompt}
        onClose={() => setPrintPrompt(null)}
        title="Venta registrada"
      >
        <div className="space-y-4">
          {/* Resumen de la venta */}
          <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-center">
            <p className="text-[11px] uppercase tracking-wide text-emerald-600 font-semibold">Venta registrada</p>
            {printPrompt?.receipt_number && (
              <p className="text-xs text-taupe-500">N° {printPrompt.receipt_number}</p>
            )}
            <p className="text-3xl font-bold text-emerald-700 tabular-nums mt-0.5">
              {formatCurrency(printPrompt?.total)}
            </p>
          </div>

          <p className="text-sm text-center text-gray-500">¿Cómo querés imprimir?</p>

          {/* Opciones (cada una sale por su impresora).
              Un comprobante sin venta solo puede salir como ticket comun: facturarlo
              ante ARCA lo convertiria en una venta y el mismo ingreso quedaria
              declarado dos veces, aca y donde se cobra de verdad. */}
          <div className={`grid gap-2.5 ${
            ((ofreceFiscalHasar ? 1 : 0) + (ofreceFiscalElectronica ? 1 : 0)) === 2
              ? 'sm:grid-cols-3'
              : (ofreceFiscalHasar || ofreceFiscalElectronica)
              ? 'sm:grid-cols-2'
              : 'grid-cols-1'
          }`}>
            {/* Ticket común -> impresora de tickets */}
            <button
              type="button"
              disabled={fiscalPrinting}
              onClick={() => {
                if (printPrompt) printSaleTicket(printPrompt, { name: tenant?.name, cuit: tenant?.cuit, address: tenant?.address });
                setPrintPrompt(null);
              }}
              className="group flex flex-col items-center text-center gap-1.5 rounded-2xl border-2 border-cream-200 bg-white hover:border-taupe-400 hover:bg-cream-50 p-4 transition-colors disabled:opacity-50"
            >
              <span className="h-11 w-11 rounded-full bg-taupe-100 group-hover:bg-taupe-200 flex items-center justify-center">
                <Printer className="h-5 w-5 text-taupe-700" />
              </span>
              <span className="font-semibold text-taupe-800">Ticket común</span>
              <span className="text-xs text-taupe-500 leading-tight">No fiscal · impresora de tickets</span>
            </button>

            {/* Comprobante fiscal -> controlador Hasar (no si ya se facturó por ARCA) */}
            {ofreceFiscalHasar && (
              <button
                type="button"
                disabled={fiscalPrinting}
                onClick={async () => {
                  if (!printPrompt) return;
                  setFiscalPrinting(true);
                  try {
                    const numero = await printFiscal(printPrompt, { cuit: tenant?.cuit });
                    // Guardar el N° de comprobante para poder reimprimirlo desde el detalle
                    if (numero) {
                      try { await salesService.setFiscalComprobante(printPrompt.id, numero); } catch { /* no bloquear la venta */ }
                    }
                    toast.success('Comprobante fiscal enviado a la Hasar');
                    setPrintPrompt(null);
                  } catch (e: any) {
                    toastFiscalError(
                      e, 'No se pudo facturar',
                      '. La venta quedó registrada; podés imprimir el ticket común.',
                    );
                  } finally {
                    setFiscalPrinting(false);
                  }
                }}
                className="group relative flex flex-col items-center text-center gap-1.5 rounded-2xl border-2 border-emerald-500 bg-emerald-600 hover:bg-emerald-700 p-4 transition-colors disabled:opacity-60 text-white shadow-sm"
              >
                <span className="h-11 w-11 rounded-full bg-white/20 flex items-center justify-center">
                  {fiscalPrinting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Receipt className="h-5 w-5" />}
                </span>
                <span className="font-semibold">{fiscalPrinting ? 'Enviando…' : 'Comprobante fiscal'}</span>
                <span className="text-xs text-emerald-50/90 leading-tight">Factura/Tique · controlador Hasar</span>
              </button>
            )}

            {/* Factura electrónica -> ARCA (WSFE), imprime en la térmica */}
            {ofreceFiscalElectronica && (
              <button
                type="button"
                disabled={emittingElectronic || fiscalPrinting}
                onClick={() => { if (printPrompt) handleEmitElectronic(printPrompt); }}
                className="group relative flex flex-col items-center text-center gap-1.5 rounded-2xl border-2 border-indigo-500 bg-indigo-600 hover:bg-indigo-700 p-4 transition-colors disabled:opacity-60 text-white shadow-sm"
              >
                <span className="h-11 w-11 rounded-full bg-white/20 flex items-center justify-center">
                  {emittingElectronic ? <Loader2 className="h-5 w-5 animate-spin" /> : <Receipt className="h-5 w-5" />}
                </span>
                <span className="font-semibold">{emittingElectronic ? 'Emitiendo…' : 'Factura electrónica'}</span>
                <span className="text-xs text-indigo-50/90 leading-tight">Factura C · ARCA (térmica)</span>
              </button>
            )}
          </div>

          {/* Pie: sin imprimir + configurar */}
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() => setPrintPrompt(null)}
              disabled={fiscalPrinting}
              className="text-sm text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              Sin imprimir
            </button>
            {tenant?.fiscal_enabled && (
              <button
                type="button"
                onClick={() => {
                  const url = window.prompt('URL del agente fiscal (Hasar) en la PC de la caja:', getFiscalAgentUrl());
                  if (url) { setFiscalAgentUrl(url); toast.success('Agente fiscal configurado'); }
                }}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Configurar impresora fiscal…
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal de cancelación */}
      <Modal
        isOpen={!!cancelConfirm}
        onClose={() => setCancelConfirm(null)}
        title="Cancelar Venta"
      >
        <div className="space-y-4">
          <p>¿Cancelar la venta <strong>{cancelConfirm?.receipt_number}</strong>?</p>
          <p className="text-sm text-taupe-500">Se restaurará el stock de los productos.</p>

          {/* Aviso fiscal: esta venta ya tiene comprobante emitido ante AFIP */}
          {cancelConfirm?.fiscal_comprobante && (
            <div className="rounded-xl border border-red-300 bg-red-50 p-3 space-y-2">
              <p className="flex items-start gap-2 text-sm text-red-700 font-medium">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                Esta venta tiene comprobante fiscal N° {cancelConfirm.fiscal_comprobante} emitido ante AFIP.
              </p>
              <p className="text-xs text-red-600">
                Para anularla legalmente hay que emitir una <strong>Nota de Crédito</strong>, no basta con cancelarla en el sistema.
              </p>
              {tenant?.fiscal_enabled && (
                <Button
                  variant="danger"
                  className="w-full"
                  disabled={fiscalPrinting || cancelSale.isPending}
                  onClick={() => { const s = cancelConfirm; setCancelConfirm(null); if (s) setNcConfirm(s); }}
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  Emitir Nota de Crédito + cancelar
                </Button>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setCancelConfirm(null)}>
              No
            </Button>
            <Button variant="danger" className="flex-1" onClick={handleCancel} disabled={cancelSale.isPending || fiscalPrinting}>
              {cancelSale.isPending
                ? 'Cancelando...'
                : cancelConfirm?.fiscal_comprobante ? 'Cancelar igual (sin NC)' : 'Sí, cancelar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Nota de Crédito — diseño */}
      <Modal
        isOpen={!!ncConfirm}
        onClose={() => { if (!fiscalPrinting) setNcConfirm(null); }}
        closeOnBackdropClick={!fiscalPrinting}
      >
        <div className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-200/60 ring-8 ring-red-50">
            <Receipt className="h-8 w-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 tracking-tight">Emitir Nota de Crédito</h3>
          <p className="mt-1 text-sm text-taupe-500">Anula el comprobante fiscal ante AFIP</p>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-red-50 border border-red-200 px-4 py-1.5">
            <span className="text-[10px] uppercase tracking-wider text-red-400 font-bold">Comprobante</span>
            <span className="text-sm font-bold text-red-700">N° {ncConfirm?.fiscal_comprobante}</span>
          </div>
        </div>

        {/* Qué va a pasar */}
        <div className="mt-6 space-y-2.5">
          {[
            { icon: Receipt, title: 'Emite la Nota de Crédito fiscal', desc: 'Revierte el comprobante ante AFIP por la Hasar' },
            { icon: XCircle, title: 'Cancela la venta', desc: 'Queda anulada en el sistema' },
            { icon: Package, title: 'Devuelve el stock', desc: 'Los productos vuelven al inventario' },
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl border border-cream-200 bg-cream-50/60 p-3">
              <span className="h-8 w-8 rounded-full bg-white border border-cream-200 flex items-center justify-center flex-shrink-0">
                <step.icon className="h-4 w-4 text-taupe-600" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-taupe-800">{step.title}</p>
                <p className="text-xs text-taupe-500">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Nota */}
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3">
          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            Después de anular, cargá la venta de nuevo con los datos correctos y volvé a facturarla.
          </p>
        </div>

        {/* Acciones */}
        <div className="mt-6 flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={() => setNcConfirm(null)} disabled={fiscalPrinting}>
            No, volver
          </Button>
          <Button
            variant="danger"
            className="flex-1"
            disabled={fiscalPrinting || cancelSale.isPending}
            onClick={async () => {
              const s = ncConfirm;
              if (!s) return;
              await emitirNotaCreditoYCancelar(s);
              setNcConfirm(null);
            }}
          >
            {fiscalPrinting
              ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Emitiendo…</>)
              : (<><Receipt className="h-4 w-4 mr-2" /> Sí, emitir NC</>)}
          </Button>
        </div>
      </Modal>

      {/* Modal armar combo (ad-hoc) */}
      <Modal
        isOpen={showComboBuilder}
        onClose={resetComboBuilder}
        title="Armar combo de promoción"
        size="lg"
      >
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Nombre del combo"
            value={comboName}
            onChange={(e) => setComboName(e.target.value)}
            className="w-full px-4 py-3 border-2 border-cream-200 rounded-xl focus:outline-none focus:border-purple-400"
            autoFocus
          />

          {/* Buscar productos */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-taupe-400" />
            <input
              type="text"
              placeholder="Buscar productos para el combo..."
              value={comboProductSearch}
              onChange={(e) => setComboProductSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-2.5 border-2 border-cream-200 rounded-xl focus:outline-none focus:border-purple-400"
            />
          </div>

          {/* Lista de productos seleccionables */}
          <div className="max-h-56 overflow-y-auto border border-cream-200 rounded-xl divide-y divide-cream-100">
            {allProducts
              .filter((p) => !p.is_combo)
              .filter((p) => {
                if (comboProductSearch.trim().length < 1) return true;
                const s = comboProductSearch.toLowerCase();
                return p.name.toLowerCase().includes(s) || (p.code?.toLowerCase().includes(s) ?? false);
              })
              .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
              .slice(0, 60)
              .map((p) => {
                const sel = comboSelections.find((s) => s.product.id === p.id);
                return (
                  <div
                    key={p.id}
                    className={`flex items-center gap-3 px-3 py-2 ${sel ? 'bg-purple-50' : 'bg-white'}`}
                  >
                    <button
                      onClick={() => toggleComboProduct(p)}
                      className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                        sel ? 'bg-purple-600 border-purple-600 text-white' : 'border-cream-300'
                      }`}
                    >
                      {sel && <Check className="h-3.5 w-3.5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-taupe-800 truncate">{p.name}</p>
                      <p className="text-xs text-taupe-400">
                        {p.sale_price != null ? formatCurrency(p.sale_price) : 'Sin precio'}
                      </p>
                    </div>
                    {sel && (() => {
                      const isWeight = (p.dimension as Dimension) === 'weight';
                      const stepQ = isWeight ? 0.1 : 1;
                      const unit = (p.display_unit as Unit) || 'unit';
                      return (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => setComboQty(p.id, Math.max(0, +(sel.quantity - stepQ).toFixed(3)))}
                            className="w-7 h-7 rounded bg-purple-100 hover:bg-purple-200 font-bold"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="0"
                            step={isWeight ? '0.01' : '1'}
                            value={sel.quantity}
                            onChange={(e) => setComboQty(p.id, parseFloat(e.target.value) || 0)}
                            className="w-14 text-center text-sm border border-cream-200 rounded py-1"
                          />
                          <span className="text-xs text-taupe-400 w-5">{UNIT_LABEL_SHORT[unit]}</span>
                          <button
                            onClick={() => setComboQty(p.id, +(sel.quantity + stepQ).toFixed(3))}
                            className="w-7 h-7 rounded bg-purple-100 hover:bg-purple-200 font-bold"
                          >
                            +
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
          </div>

          {/* Resumen y precio promo */}
          {comboSelections.length > 0 && (
            <div className="bg-cream-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm text-taupe-500">
                <span>Precio normal (suma):</span>
                <span className="line-through">{formatCurrency(comboSelTotalNormal)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <label className="text-sm font-medium text-taupe-700">Precio combo (efectivo):</label>
                <div className="w-36">
                  <CurrencyInput
                    value={comboPrice}
                    onChange={(v) => setComboPrice(v)}
                    focusRingClassName="focus:ring-purple-300 focus:border-purple-400"
                    className="text-right font-bold"
                  />
                </div>
              </div>
              <p className="text-xs text-taupe-400">
                Se descuenta el stock de cada producto elegido. El precio es único para todos los métodos de pago.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={resetComboBuilder}>
              Cancelar
            </Button>
            <Button
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              disabled={!comboName.trim() || comboSelections.length === 0 || !((parseARNumber(comboPrice) ?? 0) > 0)}
              onClick={handleAddCombo}
            >
              <Check className="h-5 w-5 mr-2" />
              Agregar combo
            </Button>
          </div>
        </div>
      </Modal>

      <DiscountReasonsModal
        isOpen={showReasonsModal}
        onClose={() => setShowReasonsModal(false)}
      />
    </div>
  );
}
